import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  Badge,
  useColorModeValue,
  Spinner,
  useToast,
  HStack,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { MdLocationOn, MdLocalShipping } from "react-icons/md";
import { baseUrl } from "../../utils/utils";
import CustomButton from "../../components/CustomButton";
import { MapPin, Pin, Truck } from "lucide-react";

const VehicleOrderMap = () => {
  const toast = useToast();
  const {
    isOpen: isOpenReassign,
    onOpen: onOpenReassign,
    onClose: onCloseReassign,
  } = useDisclosure();

  // ALL HOOKS MUST BE AT THE TOP BEFORE ANY CONDITIONAL RETURNS
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeMarker, setActiveMarker] = useState(null);
  const [vehiclesData, setVehiclesData] = useState([]);
  const [dp_list, setDp_list] = useState([]);
  const [dp_vehicle_list, setDp_vehicle_list] = useState([]);
  const [vehicle_driver_list, setVehicle_driver_list] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [directionsResponses, setDirectionsResponses] = useState({});

  // Reassign form data
  const [reassignData, setReassignData] = useState({
    order_id: "",
    dplist: "",
    vehicle: "",
    driver: "",
    dplist_error: "",
    vehicle_error: "",
    driver_error: "",
  });

  // Load Google Maps Script with required libraries
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyDF2pgkAq9rIaeQGY7HL89D6fZe70XIY2I",
    libraries: ["places", "geometry"],
  });

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.700", "gray.200");
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const infoCardBg = useColorModeValue("#E6F4FA", "blue.900");

  // Map container style - Full screen
  const mapContainerStyle = {
    width: "100%",
    height: "100%",
  };

  // Default center (will be updated based on data)
  const [center, setCenter] = useState({
    lat: 22.718435,
    lng: 75.855217,
  });

  // Google Maps options
  const options = {
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
  };

  // Calculate multi-stop route for a vehicle visiting all customers
  const calculateMultiStopRoute = async (vehicleData, vehicleColor) => {
    if (
      !window.google ||
      !vehicleData.vehicle_latlong ||
      !vehicleData.customers?.length
    )
      return;

    const directionsService = new window.google.maps.DirectionsService();

    try {
      // Parse vehicle location (origin)
      const [vLat, vLng] = vehicleData.vehicle_latlong
        .split(",")
        .map(parseFloat);
      if (isNaN(vLat) || isNaN(vLng)) return;

      const origin = { lat: vLat, lng: vLng };

      // Parse all customer locations
      const validCustomers = vehicleData.customers.filter((customer) => {
        if (!customer.customer_latlong) return false;
        const [cLat, cLng] = customer.customer_latlong
          .split(",")
          .map(parseFloat);
        return !isNaN(cLat) && !isNaN(cLng);
      });

      if (validCustomers.length === 0) return;

      // Create waypoints for all customers except the last one
      const waypoints = validCustomers.slice(0, -1).map((customer) => {
        const [cLat, cLng] = customer.customer_latlong
          .split(",")
          .map(parseFloat);
        return {
          location: { lat: cLat, lng: cLng },
          stopover: true,
        };
      });

      // Last customer is the destination
      const lastCustomer = validCustomers[validCustomers.length - 1];
      const [dLat, dLng] = lastCustomer.customer_latlong
        .split(",")
        .map(parseFloat);
      const destination = { lat: dLat, lng: dLng };

      // Request directions with waypoints
      const result = await directionsService.route({
        origin: origin,
        destination: destination,
        waypoints: waypoints,
        optimizeWaypoints: true, // This will optimize the route order
        travelMode: window.google.maps.TravelMode.DRIVING,
      });

      setDirectionsResponses((prev) => ({
        ...prev,
        [vehicleData.vehicle_id]: {
          response: result,
          color: vehicleColor,
          vehicleData: vehicleData,
        },
      }));
    } catch (error) {
      console.error("Error calculating multi-stop route:", error);
      // If multi-stop route fails, try individual routes
      calculateIndividualRoutes(vehicleData, vehicleColor);
    }
  };

  // Fallback: Calculate individual routes if multi-stop fails
  const calculateIndividualRoutes = async (vehicleData, vehicleColor) => {
    if (!window.google || !vehicleData.vehicle_latlong) return;

    const [vLat, vLng] = vehicleData.vehicle_latlong.split(",").map(parseFloat);
    if (isNaN(vLat) || isNaN(vLng)) return;

    const vehiclePosition = { lat: vLat, lng: vLng };
    const directionsService = new window.google.maps.DirectionsService();

    vehicleData.customers?.forEach(async (customer, customerIndex) => {
      if (customer.customer_latlong) {
        const [cLat, cLng] = customer.customer_latlong
          .split(",")
          .map(parseFloat);
        if (!isNaN(cLat) && !isNaN(cLng)) {
          const customerPosition = { lat: cLat, lng: cLng };

          try {
            const result = await directionsService.route({
              origin: vehiclePosition,
              destination: customerPosition,
              travelMode: window.google.maps.TravelMode.DRIVING,
            });

            const key = `${vehicleData.vehicle_id}-${customerIndex}`;
            setDirectionsResponses((prev) => ({
              ...prev,
              [key]: {
                response: result,
                color: vehicleColor,
              },
            }));
          } catch (error) {
            console.error("Error calculating individual route:", error);
          }
        }
      }
    });
  };

  // Fetch DP list for reassignment
  const fetchDPList = async () => {
    try {
      const body = new FormData();
      body.append("action", "list");
      body.append("page", 1);
      body.append("limit", 10000);

      const response = await fetch(`${baseUrl}daily_roaster.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();
      if (data.success) {
        setDp_list(data.data);
      } else {
        console.log("Failed:", data.message);
        setDp_list([]);
      }
    } catch (error) {
      console.error("Error fetching DP list:", error);
    }
  };

  // Fetch live tracking data and calculate routes
  const fetchLiveTracking = async () => {
    try {
      setLoading(true);
      const body = new FormData();
      body.append("action", "live_tracking");

      const response = await fetch(`${baseUrl}orders.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();
      if (data.success) {
        setVehiclesData(data.data);

        // Set center to first vehicle location if available
        if (data.data.length > 0 && data.data[0].vehicle_latlong) {
          const [lat, lng] = data.data[0].vehicle_latlong
            .split(",")
            .map(parseFloat);
          if (!isNaN(lat) && !isNaN(lng)) {
            setCenter({ lat, lng });
          }
        }

        // Calculate multi-stop routes for all vehicles
        if (window.google) {
          // Clear previous routes
          setDirectionsResponses({});

          data.data.forEach((vehicle, vehicleIndex) => {
            const vehicleColor = getVehicleColor(vehicleIndex);

            // Add delay to avoid rate limiting
            setTimeout(() => {
              calculateMultiStopRoute(vehicle, vehicleColor);
            }, vehicleIndex * 500);
          });
        }
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to fetch tracking data",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching live tracking:", error);
      toast({
        title: "Error",
        description: "Failed to fetch live tracking data",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveTracking();
    fetchDPList();
  }, []);

  // Recalculate routes when map is loaded
  useEffect(() => {
    if (isLoaded && vehiclesData.length > 0 && window.google) {
      // Recalculate routes after map loads
      setDirectionsResponses({});

      vehiclesData.forEach((vehicle, vehicleIndex) => {
        const vehicleColor = getVehicleColor(vehicleIndex);

        setTimeout(() => {
          calculateMultiStopRoute(vehicle, vehicleColor);
        }, vehicleIndex * 500);
      });
    }
  }, [isLoaded, vehiclesData.length]);

  // Handle map load
  const onLoad = (map) => {
    setMap(map);
  };

  // Handle Reassign button click
  const handleReassignClick = (order, vehicleData) => {
    console.log(order);
    setSelectedOrder({ ...order, currentVehicle: vehicleData });
    setReassignData({
      order_id: order.orderid,
      userid: order.userid,
      scheduletime: order.scheduletime,
      details: order.details,
      dplist: "",
      vehicle: "",
      driver: "",
      dplist_error: "",
      vehicle_error: "",
      driver_error: "",
    });
    onOpenReassign();
  };

  // Handle DP selection for reassignment
  const handleDPChange = (dpid) => {
    setReassignData({
      ...reassignData,
      dplist: dpid,
      vehicle: "",
      driver: "",
      dplist_error: "",
      vehicle_error: "",
      driver_error: "",
    });

    // Find selected DP and set vehicles
    const selectedDP = dp_list?.find((v) => v.dpid == dpid);
    setDp_vehicle_list(selectedDP?.vehicles || []);
    setVehicle_driver_list([]);
  };

  // Handle vehicle selection for reassignment
  const handleVehicleChange = (vehicleId) => {
    setReassignData({
      ...reassignData,
      vehicle: vehicleId,
      driver: "",
      vehicle_error: "",
      driver_error: "",
    });

    // Find selected vehicle and set drivers
    const selectedVehicle = dp_vehicle_list?.find(
      (v) => v.vehicle_id == vehicleId,
    );
    setVehicle_driver_list(selectedVehicle?.drivers || []);
  };

  // Handle driver selection for reassignment
  const handleDriverChange = (driverId) => {
    setReassignData({
      ...reassignData,
      driver: driverId,
      driver_error: "",
    });
  };

  // Validate reassignment form
  const validateReassignForm = () => {
    let isValid = true;
    const errors = { ...reassignData };

    if (!reassignData.dplist) {
      errors.dplist_error = "Please select a delivery partner";
      isValid = false;
    }

    if (!reassignData.vehicle) {
      errors.vehicle_error = "Please select a vehicle";
      isValid = false;
    }

    if (!reassignData.driver) {
      errors.driver_error = "Please select a driver";
      isValid = false;
    }

    setReassignData(errors);
    return isValid;
  };

  // Handle Reassignment submission
  const handleReassignSubmit = async () => {
    if (!validateReassignForm()) {
      return;
    }

    try {
      setLoading(true);
      const body = new FormData();
      body.append("action", "reassign_order");
      body.append("orderid", reassignData.order_id);
      body.append("driver_id", reassignData.driver);
      body.append("dpid", reassignData.dplist);
      body.append("vehicle_id", reassignData.vehicle);
      body.append("delivery_estimate", reassignData.scheduletime);
      body.append("userid", reassignData.userid);
      reassignData?.details?.forEach((v) => body.append("id[]", v.id));

      const response = await fetch(`${baseUrl}orders.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Success",
          description: "Order reassigned successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onCloseReassign();
        setReassignData({
          order_id: "",
          dplist: "",
          vehicle: "",
          driver: "",
          dplist_error: "",
          vehicle_error: "",
          driver_error: "",
        });
        setSelectedOrder(null);
        fetchLiveTracking();
      } else {
        toast({
          title: "Error",
          description: data.message || "Failed to reassign order",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error reassigning order:", error);
      toast({
        title: "Error",
        description: "Failed to reassign order",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate unique colors for each vehicle
  const getVehicleColor = (index) => {
    const colors = [
      "#0175c2", // Blue
      "#28a745", // Green
      "#ffc107", // Yellow
      "#dc3545", // Red
      "#6f42c1", // Purple
      "#fd7e14", // Orange
      "#20c997", // Teal
      "#e83e8c", // Pink
    ];
    return colors[index % colors.length];
  };

  // Get colored customer icon based on vehicle
  // const getCustomerIconColored = (color) => {
  //   if (!isLoaded || !window.google) return null;

  //   return {
  //     url:
  //       "data:image/svg+xml;charset=UTF-8," +
  //       encodeURIComponent(`
  //         <svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
  //           <path d="M15 0C8.373 0 3 5.373 3 12c0 8.5 12 28 12 28s12-19.5 12-28c0-6.627-5.373-12-12-12z" fill="${color}"/>
  //           <circle cx="15" cy="12" r="6" fill="white"/>
  //         </svg>
  //       `),
  //     scaledSize: new window.google.maps.Size(30, 40),
  //   };
  // };

  const getCustomerIconColored = (color, number) => {
    if (!isLoaded || !window.google) return null;

    return {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
          <!-- Pin Shape -->
          <path d="M20 0C10 0 3 7 3 16c0 11 17 34 17 34s17-23 17-34C37 7 30 0 20 0z" fill="${color}"/>

    

          <!-- Number -->
          <text 
            x="20" 
            y="21" 
            text-anchor="middle" 
            font-size="12" 
            font-weight="bold" 
            fill="white"
            font-family="Arial"
          >
            ${number}
          </text>
        </svg>
      `),
      scaledSize: new window.google.maps.Size(40, 50),
    };
  };

  // Get colored vehicle icon
  const getVehicleIconColored = (color) => {
    if (!isLoaded || !window.google) return null;

    return {
      url:
        "data:image/svg+xml;charset=UTF-8," +
        encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 24 24" fill="${color}">
            <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
          </svg>
        `),
      scaledSize: new window.google.maps.Size(45, 45),
    };
  };

  // CONDITIONAL RETURNS AFTER ALL HOOKS
  if (loadError) {
    return (
      <Flex h="100vh" align="center" justify="center" bg={bgColor}>
        <VStack spacing={4}>
          <Heading color="red.500" size="lg">
            Error Loading Maps
          </Heading>
          <Text color={textColor}>
            Failed to load Google Maps. Please check your API key and internet
            connection.
          </Text>
        </VStack>
      </Flex>
    );
  }

  if (!isLoaded) {
    return (
      <Flex h="100vh" align="center" justify="center" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text color={textColor} fontSize="lg">
            Loading Google Maps...
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box h="100vh" position="relative">
      {/* Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={12}
        options={options}
        onLoad={onLoad}
      >
        {loading && vehiclesData.length === 0 ? (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            bg={cardBg}
            p={6}
            borderRadius="lg"
            boxShadow="lg"
          >
            <VStack spacing={3}>
              <Spinner size="lg" color="blue.500" />
              <Text color={textColor}>Loading tracking data...</Text>
            </VStack>
          </Box>
        ) : vehiclesData.length === 0 ? (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            bg={cardBg}
            p={6}
            borderRadius="lg"
            boxShadow="lg"
          >
            <VStack spacing={3}>
              <Heading size="md" color={headingColor}>
                No Vehicles Found
              </Heading>
              <Text color={textColor}>
                There are no vehicles with location data to display
              </Text>
            </VStack>
          </Box>
        ) : (
          <>
            {/* Render Direction Routes */}
            {Object.entries(directionsResponses).map(([key, data]) => (
              <DirectionsRenderer
                key={key}
                directions={data.response}
                options={{
                  suppressMarkers: true, // We'll use custom markers
                  polylineOptions: {
                    strokeColor: data.color,
                    strokeOpacity: 0.8,
                    strokeWeight: 5,
                  },
                }}
              />
            ))}

            {/* Loop through each vehicle */}
            {vehiclesData.map((vehicle, vehicleIndex) => {
              const vehicleColor = getVehicleColor(vehicleIndex);

              // Parse vehicle location
              if (!vehicle.vehicle_latlong) return null;
              const [vLat, vLng] = vehicle.vehicle_latlong
                .split(",")
                .map(parseFloat);
              if (isNaN(vLat) || isNaN(vLng)) return null;

              const vehiclePosition = { lat: vLat, lng: vLng };

              // Track used coordinates to detect overlaps and add small offset (3-4 meters)
              const usedCoordinates = new Map();

              return (
                <React.Fragment key={`vehicle-${vehicle.vehicle_id}`}>
                  {/* Vehicle Marker */}
                  <Marker
                    position={vehiclePosition}
                    icon={getVehicleIconColored(vehicleColor)}
                    onClick={() =>
                      setActiveMarker(`vehicle-${vehicle.vehicle_id}`)
                    }
                    zIndex={1000}
                  >
                    {activeMarker === `vehicle-${vehicle.vehicle_id}` && (
                      <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                        <Box p={2} minW="200px">
                          <VStack align="start" spacing={1}>
                            <Heading size="sm" color={headingColor}>
                              Vehicle {vehicle.vehicleno}
                            </Heading>
                            <Text fontSize="sm" color={textColor}>
                              Driver: {vehicle.driver_name || "Not Assigned"}
                            </Text>
                            <Text fontSize="sm" color={textColor}>
                              DP:{" "}
                              {vehicle.delivery_partner_name || "Not Assigned"}
                            </Text>
                            <Text fontSize="xs" color={textColor}>
                              Updated: {vehicle.last_updated}
                            </Text>
                            <Badge colorScheme="blue">
                              {vehicle.customers?.length || 0} Customer(s)
                            </Badge>
                          </VStack>
                        </Box>
                      </InfoWindow>
                    )}
                  </Marker>

                  {/* Customer Markers for this vehicle */}
                  {vehicle.customers?.map((customer, customerIndex) => {
                    // Parse customer location
                    if (!customer.customer_latlong) {
                      console.log(`Customer ${customerIndex} (${customer.customer_name}): No latlong`);
                      return null;
                    }
                    const [cLat, cLng] = customer.customer_latlong
                      .split(",")
                      .map(parseFloat);
                    if (isNaN(cLat) || isNaN(cLng)) {
                      // console.log(`Customer ${customerIndex} (${customer.customer_name}): Invalid coordinates - ${customer.customer_latlong}`);
                      return null;
                    }

                    // Check for overlapping coordinates and add small offset (3-4 meters) if needed
                    const coordKey = `${cLat.toFixed(6)},${cLng.toFixed(6)}`;
                    let offsetLat = 0;
                    let offsetLng = 0;

                    if (usedCoordinates.has(coordKey)) {
                      const overlapCount = usedCoordinates.get(coordKey);
                      // Add small offset (~0.00004 = ~3-4 meters) based on overlap count
                      offsetLat = (overlapCount * 0.00001);
                      offsetLng = (overlapCount * 0.00001);
                      usedCoordinates.set(coordKey, overlapCount + 1);
                    } else {
                      usedCoordinates.set(coordKey, 1);
                    }

                    const customerPosition = { 
                      lat: cLat + offsetLat, 
                      lng: cLng + offsetLng 
                    };

                    return (
                      <Marker
                        key={`customer-${vehicle.vehicle_id}-${customerIndex}`}
                        position={customerPosition}
                        icon={getCustomerIconColored(
                          vehicleColor,
                          customerIndex + 1,
                        )}
                        onClick={() =>
                          setActiveMarker(
                            `customer-${vehicle.vehicle_id}-${customerIndex}`,
                          )
                        }
                        zIndex={999}
                      >
                        {activeMarker ===
                          `customer-${vehicle.vehicle_id}-${customerIndex}` && (
                            <InfoWindow
                              onCloseClick={() => setActiveMarker(null)}
                            >
                              <Box p={2} minW="250px">
                                <VStack align="start" spacing={2}>
                                  <Heading size="sm" color={headingColor}>
                                    Customer
                                  </Heading>
                                  <Badge colorScheme="purple">
                                    Order: {customer.orderid}
                                  </Badge>
                                  <Text fontSize="sm" color={textColor}>
                                    {customer.customer_name || "Unknown Customer"}
                                  </Text>
                                  <Text fontSize="xs" color={textColor}>
                                    {customer.shipping_address ||
                                      customer.address_id ||
                                      "Address not available"}
                                  </Text>
                                  <Divider />
                                  <Text fontSize="xs" color={textColor}>
                                    Assigned Vehicle: {vehicle.vehicleno}
                                  </Text>
                                  <CustomButton
                                    b_name="Reassign"
                                    onClick={() =>
                                      handleReassignClick(customer, vehicle)
                                    }
                                    b_color="#0175c2"
                                    height="28px"
                                  />
                                </VStack>
                              </Box>
                            </InfoWindow>
                          )}
                      </Marker>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Legend Overlay on Map */}
            <Box
              position="absolute"
              bottom="10px"
              left="10px"
              bg={cardBg}
              p={4}
              borderRadius="lg"
              boxShadow="lg"
              borderWidth="1px"
              borderColor={borderColor}
              maxW="250px"
            >
              <VStack align="start" spacing={3}>
                <Heading size="sm" color={headingColor}>
                  Map Legend
                </Heading>
                <Divider />
                <HStack spacing={3}>
                  <Truck size={20} color="#0175c2" />
                  <Text fontSize="sm" color={textColor}>
                    Vehicle Location
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <MapPin size={20} color="#0175c2" />
                  <Text fontSize="sm" color={textColor}>
                    Customer Location
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <Box w="20px" h="4px" bg="#0175c2" borderRadius="sm" />
                  <Text fontSize="sm" color={textColor}>
                    Delivery Route
                  </Text>
                </HStack>
                <Text fontSize="xs" color={textColor} fontStyle="italic">
                  Each color represents a vehicle's optimized multi-stop
                  delivery route
                </Text>
              </VStack>
            </Box>
          </>
        )}
      </GoogleMap>

      {/* Reassign Modal */}
      <Modal
        isOpen={isOpenReassign}
        onClose={onCloseReassign}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reassign Order</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Current Assignment Info */}
              <Box
                p={4}
                bg={infoCardBg}
                borderRadius="md"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Heading size="sm" mb={2} color={headingColor}>
                  Current Assignment
                </Heading>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color={textColor}>
                    Order: {selectedOrder?.orderid}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Vehicle: {selectedOrder?.currentVehicle?.vehicleno}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Driver:{" "}
                    {selectedOrder?.currentVehicle?.driver_name || "N/A"}
                  </Text>
                </VStack>
              </Box>

              {/* Delivery Partner Selection */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">
                  Select New Delivery Partner
                </FormLabel>
                <Select
                  value={reassignData.dplist}
                  onChange={(e) => handleDPChange(e.target.value)}
                  placeholder="Choose delivery partner"
                  size="md"
                  w="full"
                  borderColor={
                    reassignData.dplist_error ? "red.300" : borderColor
                  }
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px blue.500",
                  }}
                >
                  {dp_list?.map((item) => (
                    <option key={item.dpid} value={item.dpid}>
                      {item?.dp_name}
                    </option>
                  ))}
                </Select>
                {reassignData.dplist_error && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    ⚠️ {reassignData.dplist_error}
                  </Text>
                )}
              </FormControl>

              {/* Vehicle Selection */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">
                  Select New Vehicle
                </FormLabel>
                <Select
                  value={reassignData.vehicle}
                  onChange={(e) => handleVehicleChange(e.target.value)}
                  placeholder="Select vehicle"
                  size="md"
                  w="full"
                  disabled={!reassignData.dplist}
                  borderColor={
                    reassignData.vehicle_error ? "red.300" : borderColor
                  }
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px blue.500",
                  }}
                >
                  {dp_vehicle_list?.map((item) => (
                    <option key={item.vehicle_id} value={item.vehicle_id}>
                      {item.vehicle_no}
                    </option>
                  ))}
                </Select>
                {reassignData.vehicle_error && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    ⚠️ {reassignData.vehicle_error}
                  </Text>
                )}
              </FormControl>

              {/* Driver Selection */}
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">
                  Select New Driver
                </FormLabel>
                <Select
                  value={reassignData.driver}
                  onChange={(e) => handleDriverChange(e.target.value)}
                  placeholder="Choose driver"
                  size="md"
                  w="full"
                  disabled={!reassignData.vehicle}
                  borderColor={
                    reassignData.driver_error ? "red.300" : borderColor
                  }
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px blue.500",
                  }}
                >
                  {vehicle_driver_list?.map((item) => (
                    <option key={item.driver_id} value={item.driver_id}>
                      {item.driver_name}
                    </option>
                  ))}
                </Select>
                {reassignData.driver_error && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    ⚠️ {reassignData.driver_error}
                  </Text>
                )}
              </FormControl>

              {/* Customer Information */}
              <Box
                p={4}
                bg={bgColor}
                borderRadius="md"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Heading size="sm" mb={2} color={headingColor}>
                  Customer Information
                </Heading>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color={textColor}>
                    Name: {selectedOrder?.customer_name || "Unknown"}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Address:{" "}
                    {selectedOrder?.shipping_address ||
                      selectedOrder?.address_id ||
                      "N/A"}
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              onClick={() => {
                onCloseReassign();
                setReassignData({
                  order_id: "",
                  dplist: "",
                  vehicle: "",
                  driver: "",
                  dplist_error: "",
                  vehicle_error: "",
                  driver_error: "",
                });
                setSelectedOrder(null);
              }}
              variant="outline"
              size="md"
              mr={3}
            >
              Cancel
            </Button>
            <CustomButton
              b_name={loading ? "Reassigning..." : "Reassign Order"}
              onClick={handleReassignSubmit}
              b_color="#0175c2"
              height="40px"
              isDisabled={loading}
            />
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VehicleOrderMap;
// import React, { useEffect, useState } from "react";
// import {
//   Box,
//   Flex,
//   Heading,
//   Text,
//   VStack,
//   Badge,
//   useColorModeValue,
//   Spinner,
//   useToast,
//   HStack,
//   Divider,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalBody,
//   ModalFooter,
//   ModalCloseButton,
//   Button,
//   FormControl,
//   FormLabel,
//   Select,
//   useDisclosure,
// } from "@chakra-ui/react";
// import {
//   GoogleMap,
//   useLoadScript,
//   Marker,
//   InfoWindow,
// } from "@react-google-maps/api";
// import { MdLocationOn, MdLocalShipping } from "react-icons/md";
// import { baseUrl } from "../../utils/utils";
// import CustomButton from "../../components/CustomButton";
// import { MapPin, Pin, Truck } from "lucide-react";

// const VehicleOrderMap = () => {
//   const toast = useToast();
//   const {
//     isOpen: isOpenReassign,
//     onOpen: onOpenReassign,
//     onClose: onCloseReassign,
//   } = useDisclosure();

//   // ALL HOOKS MUST BE AT THE TOP BEFORE ANY CONDITIONAL RETURNS
//   const [map, setMap] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [activeMarker, setActiveMarker] = useState(null);
//   const [vehiclesData, setVehiclesData] = useState([]);
//   const [dp_list, setDp_list] = useState([]);
//   const [dp_vehicle_list, setDp_vehicle_list] = useState([]);
//   const [vehicle_driver_list, setVehicle_driver_list] = useState([]);
//   const [selectedOrder, setSelectedOrder] = useState(null);

//   // Reassign form data
//   const [reassignData, setReassignData] = useState({
//     order_id: "",
//     dplist: "",
//     vehicle: "",
//     driver: "",
//     dplist_error: "",
//     vehicle_error: "",
//     driver_error: "",
//   });

//   // Load Google Maps Script
//   const { isLoaded, loadError } = useLoadScript({
//     googleMapsApiKey: "AIzaSyDF2pgkAq9rIaeQGY7HL89D6fZe70XIY2I",
//   });

//   // Color mode values
//   const cardBg = useColorModeValue("white", "gray.800");
//   const borderColor = useColorModeValue("gray.200", "gray.600");
//   const textColor = useColorModeValue("gray.600", "gray.400");
//   const headingColor = useColorModeValue("gray.700", "gray.200");
//   const bgColor = useColorModeValue("gray.50", "gray.900");
//   const infoCardBg = useColorModeValue("#E6F4FA", "blue.900");

//   // Map container style - Full screen
//   const mapContainerStyle = {
//     width: "100%",
//     height: "100%",
//   };

//   // Default center (will be updated based on data)
//   const [center, setCenter] = useState({
//     lat: 22.718435,
//     lng: 75.855217,
//   });

//   // Google Maps options
//   const options = {
//     disableDefaultUI: false,
//     zoomControl: true,
//     streetViewControl: false,
//     mapTypeControl: false,
//     fullscreenControl: true,
//   };

//   // Fetch DP list for reassignment
//   const fetchDPList = async () => {
//     try {
//       const body = new FormData();
//       body.append("action", "list");
//       body.append("page", 1);
//       body.append("limit", 10000);

//       const response = await fetch(`${baseUrl}daily_roaster.php`, {
//         method: "POST",
//         body: body,
//       });

//       const data = await response.json();

//       if (data.success) {
//         setDp_list(data.data);
//       } else {
//         console.log("Failed:", data.message);
//         setDp_list([]);
//       }
//     } catch (error) {
//       console.error("Error fetching DP list:", error);
//     }
//   };

//   // Fetch live tracking data
//   const fetchLiveTracking = async () => {
//     try {
//       setLoading(true);
//       const body = new FormData();
//       body.append("action", "live_tracking");

//       const response = await fetch(`${baseUrl}orders.php`, {
//         method: "POST",
//         body: body,
//       });

//       const data = await response.json();

//       if (data.success) {
//         setVehiclesData(data.data);

//         // Set center to first vehicle location if available
//         if (data.data.length > 0 && data.data[0].vehicle_latlong) {
//           const [lat, lng] = data.data[0].vehicle_latlong
//             .split(",")
//             .map(parseFloat);
//           if (!isNaN(lat) && !isNaN(lng)) {
//             setCenter({ lat, lng });
//           }
//         }
//       } else {
//         toast({
//           title: "Error",
//           description: data.message || "Failed to fetch tracking data",
//           status: "error",
//           duration: 3000,
//           isClosable: true,
//         });
//       }
//     } catch (error) {
//       console.error("Error fetching live tracking:", error);
//       toast({
//         title: "Error",
//         description: "Failed to fetch live tracking data",
//         status: "error",
//         duration: 3000,
//         isClosable: true,
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchLiveTracking();
//     fetchDPList();
//   }, []);

//   // Handle map load
//   const onLoad = (map) => {
//     setMap(map);
//   };

//   // Handle Reassign button click
//   const handleReassignClick = (order, vehicleData) => {
//     console.log(order);

//     setSelectedOrder({ ...order, currentVehicle: vehicleData });
//     setReassignData({
//       order_id: order.orderid,
//       userid: order.userid,
//       scheduletime: order.scheduletime,
//       details: order.details,
//       dplist: "",
//       vehicle: "",
//       driver: "",
//       dplist_error: "",
//       vehicle_error: "",
//       driver_error: "",
//     });
//     onOpenReassign();
//   };

//   // Handle DP selection for reassignment
//   const handleDPChange = (dpid) => {
//     setReassignData({
//       ...reassignData,
//       dplist: dpid,
//       vehicle: "",
//       driver: "",
//       dplist_error: "",
//       vehicle_error: "",
//       driver_error: "",
//     });

//     // Find selected DP and set vehicles
//     const selectedDP = dp_list?.find((v) => v.dpid == dpid);
//     setDp_vehicle_list(selectedDP?.vehicles || []);
//     setVehicle_driver_list([]);
//   };

//   // Handle vehicle selection for reassignment
//   const handleVehicleChange = (vehicleId) => {
//     setReassignData({
//       ...reassignData,
//       vehicle: vehicleId,
//       driver: "",
//       vehicle_error: "",
//       driver_error: "",
//     });

//     // Find selected vehicle and set drivers
//     const selectedVehicle = dp_vehicle_list?.find(
//       (v) => v.vehicle_id == vehicleId,
//     );
//     setVehicle_driver_list(selectedVehicle?.drivers || []);
//   };

//   // Handle driver selection for reassignment
//   const handleDriverChange = (driverId) => {
//     setReassignData({
//       ...reassignData,
//       driver: driverId,
//       driver_error: "",
//     });
//   };

//   // Validate reassignment form
//   const validateReassignForm = () => {
//     let isValid = true;
//     const errors = { ...reassignData };

//     if (!reassignData.dplist) {
//       errors.dplist_error = "Please select a delivery partner";
//       isValid = false;
//     }
//     if (!reassignData.vehicle) {
//       errors.vehicle_error = "Please select a vehicle";
//       isValid = false;
//     }
//     if (!reassignData.driver) {
//       errors.driver_error = "Please select a driver";
//       isValid = false;
//     }

//     setReassignData(errors);
//     return isValid;
//   };
//   console.log("reassignData", reassignData);

//   // Handle Reassignment submission
//   const handleReassignSubmit = async () => {
//     if (!validateReassignForm()) {
//       return;
//     }

//     try {
//       setLoading(true);
//       const body = new FormData();
//       body.append("action", "reassign_order"); // You may need to adjust the action name based on your API
//       body.append("orderid", reassignData.order_id);
//       body.append("driver_id", reassignData.driver);
//       body.append("dpid", reassignData.dplist);
//       body.append("vehicle_id", reassignData.vehicle);
//       body.append("delivery_estimate", reassignData.scheduletime);
//       body.append("userid", reassignData.userid);
//       reassignData?.details?.forEach((v) => body.append("id[]", v.id));

//       const response = await fetch(`${baseUrl}orders.php`, {
//         method: "POST",
//         body: body,
//       });

//       const data = await response.json();

//       if (data.success) {
//         toast({
//           title: "Success",
//           description: "Order reassigned successfully",
//           status: "success",
//           duration: 3000,
//           isClosable: true,
//         });
//         onCloseReassign();
//         setReassignData({
//           order_id: "",
//           dplist: "",
//           vehicle: "",
//           driver: "",
//           dplist_error: "",
//           vehicle_error: "",
//           driver_error: "",
//         });
//         setSelectedOrder(null);
//         fetchLiveTracking(); // Refresh the map
//       } else {
//         toast({
//           title: "Error",
//           description: data.message || "Failed to reassign order",
//           status: "error",
//           duration: 3000,
//           isClosable: true,
//         });
//       }
//     } catch (error) {
//       console.error("Error reassigning order:", error);
//       toast({
//         title: "Error",
//         description: "Failed to reassign order",
//         status: "error",
//         duration: 3000,
//         isClosable: true,
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Generate unique colors for each vehicle
//   const getVehicleColor = (index) => {
//     const colors = [
//       "#0175c2", // Blue
//       "#28a745", // Green
//       "#ffc107", // Yellow
//       "#dc3545", // Red
//       "#6f42c1", // Purple
//       "#fd7e14", // Orange
//       "#20c997", // Teal
//       "#e83e8c", // Pink
//     ];
//     return colors[index % colors.length];
//   };

//   // Get colored customer icon based on vehicle
//   const getCustomerIconColored = (color) => {
//     if (!isLoaded || !window.google) return null;

//     return {
//       url:
//         "data:image/svg+xml;charset=UTF-8," +
//         encodeURIComponent(`
//         <svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
//           <path d="M15 0 C7.5 0 1.5 6 1.5 13.5 C1.5 21 15 40 15 40 C15 40 28.5 21 28.5 13.5 C28.5 6 22.5 0 15 0 Z" fill="${color}" stroke="white" stroke-width="2"/>
//           <circle cx="15" cy="13.5" r="6" fill="white"/>
//         </svg>
//       `),
//       scaledSize: new window.google.maps.Size(30, 40),
//     };
//   };

//   // Get colored vehicle icon
//   const getVehicleIconColored = (color) => {
//     if (!isLoaded || !window.google) return null;

//     return {
//       url:
//         "data:image/svg+xml;charset=UTF-8," +
//         encodeURIComponent(`
//       <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 45 45">
//         <circle cx="22.5" cy="22.5" r="20" fill="${color}" stroke="white" stroke-width="3"/>
//         <g transform="translate(13.5, 13.5) scale(0.75)">
//           <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
//           <path d="M15 18H9" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
//           <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
//           <circle cx="17" cy="18" r="2" stroke="#fff" stroke-width="2" fill="none"/>
//           <circle cx="7" cy="18" r="2" stroke="#fff" stroke-width="2" fill="none"/>
//         </g>
//       </svg>
//     `),
//       scaledSize: new window.google.maps.Size(45, 45),
//     };
//   };

//   // CONDITIONAL RETURNS AFTER ALL HOOKS
//   if (loadError) {
//     return (
//       <Box p={4} textAlign="center">
//         <Heading size="md" color="red.500" mb={2}>
//           Error Loading Maps
//         </Heading>
//         <Text color={textColor}>
//           Failed to load Google Maps. Please check your API key and internet
//           connection.
//         </Text>
//       </Box>
//     );
//   }

//   if (!isLoaded) {
//     return (
//       <Flex justify="center" align="center" minH="100vh">
//         <VStack>
//           <Spinner size="xl" color="#0175c2" thickness="4px" />
//           <Text mt={4} color={textColor}>
//             Loading Google Maps...
//           </Text>
//         </VStack>
//       </Flex>
//     );
//   }

//   return (
//     <Box position="relative" w="100%" h="100vh">
//       {/* Map */}
//       {loading && vehiclesData.length === 0 ? (
//         <Flex justify="center" align="center" h="100vh" bg={bgColor}>
//           <VStack>
//             <Spinner size="xl" color="#0175c2" thickness="4px" />
//             <Text mt={4} color={textColor}>
//               Loading tracking data...
//             </Text>
//           </VStack>
//         </Flex>
//       ) : vehiclesData.length === 0 ? (
//         <Flex justify="center" align="center" h="100vh" bg={bgColor}>
//           <VStack>
//             <MdLocationOn size={64} color="#0175c2" />
//             <Heading size="md" color={headingColor} mt={4}>
//               No Vehicles Found
//             </Heading>
//             <Text color={textColor}>
//               There are no vehicles with location data to display
//             </Text>
//           </VStack>
//         </Flex>
//       ) : (
//         <>
//           <GoogleMap
//             mapContainerStyle={mapContainerStyle}
//             center={center}
//             zoom={12}
//             options={options}
//             onLoad={onLoad}
//           >
//             {/* Loop through each vehicle */}
//             {vehiclesData.map((vehicle, vehicleIndex) => {
//               const vehicleColor = getVehicleColor(vehicleIndex);

//               // Parse vehicle location
//               if (!vehicle.vehicle_latlong) return null;
//               const [vLat, vLng] = vehicle.vehicle_latlong
//                 .split(",")
//                 .map(parseFloat);
//               if (isNaN(vLat) || isNaN(vLng)) return null;

//               return (
//                 <React.Fragment key={`vehicle-group-${vehicleIndex}`}>
//                   {/* Vehicle Marker */}
//                   <Marker
//                     position={{ lat: vLat, lng: vLng }}
//                     icon={getVehicleIconColored(vehicleColor)}
//                     onClick={() =>
//                       setActiveMarker(`vehicle-${vehicle.vehicle_id}`)
//                     }
//                   >
//                     {activeMarker === `vehicle-${vehicle.vehicle_id}` && (
//                       <InfoWindow onCloseClick={() => setActiveMarker(null)}>
//                         <Box p={2} maxW="250px">
//                           <Flex align="center" gap={2} mb={2}>
//                             <Badge
//                               bg={vehicleColor}
//                               color="white"
//                               fontSize="10px"
//                             >
//                               Vehicle
//                             </Badge>
//                           </Flex>
//                           <Heading size="xs" mb={1} color={vehicleColor}>
//                             {vehicle.vehicleno}
//                           </Heading>
//                           <Text fontSize="xs" mb={1}>
//                             Driver: {vehicle.driver_name || "Not Assigned"}
//                           </Text>
//                           <Text fontSize="xs" mb={1}>
//                             DP:{" "}
//                             {vehicle.delivery_partner_name || "Not Assigned"}
//                           </Text>
//                           <Text fontSize="9px" color="gray.500">
//                             Updated: {vehicle.last_updated}
//                           </Text>
//                           <Badge colorScheme="green" fontSize="9px" mt={1}>
//                             {vehicle.customers?.length || 0} Customer(s)
//                           </Badge>
//                         </Box>
//                       </InfoWindow>
//                     )}
//                   </Marker>

//                   {/* Customer Markers for this vehicle */}
//                   {vehicle.customers?.map((customer, customerIndex) => {
//                     // Parse customer location
//                     if (!customer.customer_latlong) return null;
//                     const [cLat, cLng] = customer.customer_latlong
//                       .split(",")
//                       .map(parseFloat);
//                     if (isNaN(cLat) || isNaN(cLng)) return null;

//                     return (
//                       <Marker
//                         key={`customer-${vehicle.vehicle_id}-${customerIndex}`}
//                         position={{ lat: cLat, lng: cLng }}
//                         icon={getCustomerIconColored(vehicleColor)}
//                         onClick={() =>
//                           setActiveMarker(
//                             `customer-${vehicle.vehicle_id}-${customerIndex}`,
//                           )
//                         }
//                       >
//                         {activeMarker ===
//                           `customer-${vehicle.vehicle_id}-${customerIndex}` && (
//                           <InfoWindow
//                             onCloseClick={() => setActiveMarker(null)}
//                           >
//                             <Box p={2} maxW="250px">
//                               <Flex align="center" gap={2} mb={2}>
//                                 <Badge
//                                   bg={vehicleColor}
//                                   color="white"
//                                   fontSize="10px"
//                                 >
//                                   Customer
//                                 </Badge>
//                               </Flex>
//                               <Heading size="xs" mb={1} color={vehicleColor}>
//                                 Order: {customer.orderid}
//                               </Heading>
//                               <Text fontSize="xs" mb={1} fontWeight="bold">
//                                 {customer.customer_name || "Unknown Customer"}
//                               </Text>
//                               <Text
//                                 fontSize="xs"
//                                 mb={1}
//                                 color="gray.600"
//                                 noOfLines={3}
//                               >
//                                 {customer.shipping_address ||
//                                   customer.address_id ||
//                                   "Address not available"}
//                               </Text>
//                               <Divider my={1} />
//                               <Text fontSize="9px" color="gray.500">
//                                 Assigned Vehicle: {vehicle.vehicleno}
//                               </Text>
//                               <HStack mt={2}>
//                                 <CustomButton
//                                   title="Re-Assign"
//                                   onClick={() =>
//                                     handleReassignClick(customer, vehicle)
//                                   }
//                                   b_color="#0175c2"
//                                   height="28px"
//                                 />
//                               </HStack>
//                             </Box>
//                           </InfoWindow>
//                         )}
//                       </Marker>
//                     );
//                   })}
//                 </React.Fragment>
//               );
//             })}
//           </GoogleMap>

//           {/* Legend Overlay on Map */}
//           <Box
//             position="absolute"
//             bottom="20px"
//             left="5"
//             // left="50%"
//             // transform="translateX(-50%)"
//             bg="white"
//             px={6}
//             py={3}
//             borderRadius="lg"
//             boxShadow="lg"
//             border="1px solid"
//             borderColor={borderColor}
//             zIndex={1000}
//           >
//             <Heading size="sm" mb={2} color={headingColor}>
//               Map Legend
//             </Heading>
//             <HStack spacing={6}>
//               <Flex align="center" gap={2}>
//                 <Truck color="#0175c2" />
//                 {/* <Box
//                   w="24px"
//                   h="24px"
//                   bg="#0175c2"
//                   borderRadius="full"
//                   border="2px solid white"
//                   boxShadow="sm"
//                 /> */}
//                 <Text fontSize="sm" color={textColor} fontWeight="medium">
//                   Vehicle Location
//                 </Text>
//               </Flex>
//               <Flex align="center" gap={2}>
//                 {/* <Box
//                   w="20px"
//                   h="26px"
//                   bg="#0175c2"
//                   clipPath="polygon(50% 0%, 100% 38%, 100% 100%, 0% 100%, 0% 38%)"
//                   border="2px solid white"
//                   boxShadow="sm"
//                 /> */}
//                 <MapPin color="#0175c2" />
//                 <Text fontSize="sm" color={textColor} fontWeight="medium">
//                   Customer Location
//                 </Text>
//               </Flex>
//             </HStack>
//             <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
//               Each color represents a different vehicle and its assigned
//               customers
//             </Text>
//           </Box>
//         </>
//       )}

//       {/* Reassign Modal */}
//       <Modal
//         isOpen={isOpenReassign}
//         onClose={onCloseReassign}
//         isCentered
//         size="xl"
//       >
//         <ModalOverlay bg="blackAlpha.600" />
//         <ModalContent borderRadius="2xl">
//           <ModalHeader
//             borderBottom="1px solid"
//             borderColor={borderColor}
//             bg={cardBg}
//             borderTopRadius="2xl"
//           >
//             <Flex align="center">
//               <Box fontSize="xl" color="blue.500" mr={3}>
//                 <MdLocalShipping />
//               </Box>
//               <Heading fontSize="lg">Reassign Order</Heading>
//             </Flex>
//           </ModalHeader>
//           <ModalCloseButton />
//           <ModalBody py={6}>
//             <Flex flexDirection="column" gap={4} w="full">
//               {/* Current Assignment Info */}
//               <Box
//                 p={4}
//                 bg={infoCardBg}
//                 borderRadius="lg"
//                 border="1px solid"
//                 borderColor="blue.200"
//               >
//                 <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={2}>
//                   Current Assignment
//                 </Text>
//                 <Text fontSize="xs" mb={1}>
//                   Order: {selectedOrder?.orderid}
//                 </Text>
//                 <Text fontSize="xs" mb={1}>
//                   Vehicle: {selectedOrder?.currentVehicle?.vehicleno}
//                 </Text>
//                 <Text fontSize="xs">
//                   Driver: {selectedOrder?.currentVehicle?.driver_name || "N/A"}
//                 </Text>
//               </Box>

//               <FormControl display="flex" flexDirection="column" gap={4}>
//                 {/* Delivery Partner Selection */}
//                 <Box>
//                   <FormLabel mb={2} fontWeight="semibold" color={headingColor}>
//                     Select New Delivery Partner
//                   </FormLabel>
//                   <Select
//                     value={reassignData.dplist || ""}
//                     onChange={(e) => handleDPChange(e.target.value)}
//                     placeholder="Choose delivery partner"
//                     size="md"
//                     w="full"
//                     borderColor={
//                       reassignData.dplist_error ? "red.300" : borderColor
//                     }
//                     _focus={{
//                       borderColor: "blue.500",
//                       boxShadow: "0 0 0 1px blue.500",
//                     }}
//                   >
//                     {dp_list?.map((item) => (
//                       <option key={item?.dpid} value={item?.dpid}>
//                         {item?.dp_name}
//                       </option>
//                     ))}
//                   </Select>
//                   {reassignData.dplist_error && (
//                     <Text
//                       color="red.500"
//                       fontSize="sm"
//                       mt={1}
//                       display="flex"
//                       alignItems="center"
//                     >
//                       ⚠️ {reassignData.dplist_error}
//                     </Text>
//                   )}
//                 </Box>

//                 {/* Vehicle Selection */}
//                 <Box>
//                   <FormLabel mb={2} fontWeight="semibold" color={headingColor}>
//                     Select New Vehicle
//                   </FormLabel>
//                   <Select
//                     value={reassignData.vehicle || ""}
//                     onChange={(e) => handleVehicleChange(e.target.value)}
//                     placeholder="Select vehicle"
//                     size="md"
//                     w="full"
//                     disabled={!reassignData.dplist}
//                     borderColor={
//                       reassignData.vehicle_error ? "red.300" : borderColor
//                     }
//                     _focus={{
//                       borderColor: "blue.500",
//                       boxShadow: "0 0 0 1px blue.500",
//                     }}
//                   >
//                     {dp_vehicle_list?.map((item) => (
//                       <option key={item.vehicle_id} value={item.vehicle_id}>
//                         {item.vehicle_no}
//                       </option>
//                     ))}
//                   </Select>
//                   {reassignData.vehicle_error && (
//                     <Text
//                       color="red.500"
//                       fontSize="sm"
//                       mt={1}
//                       display="flex"
//                       alignItems="center"
//                     >
//                       ⚠️ {reassignData.vehicle_error}
//                     </Text>
//                   )}
//                 </Box>

//                 {/* Driver Selection */}
//                 <Box>
//                   <FormLabel mb={2} fontWeight="semibold" color={headingColor}>
//                     Select New Driver
//                   </FormLabel>
//                   <Select
//                     value={reassignData.driver || ""}
//                     onChange={(e) => handleDriverChange(e.target.value)}
//                     placeholder="Choose driver"
//                     size="md"
//                     w="full"
//                     disabled={!reassignData.vehicle}
//                     borderColor={
//                       reassignData.driver_error ? "red.300" : borderColor
//                     }
//                     _focus={{
//                       borderColor: "blue.500",
//                       boxShadow: "0 0 0 1px blue.500",
//                     }}
//                   >
//                     {vehicle_driver_list?.map((item) => (
//                       <option key={item.driver_id} value={item.driver_id}>
//                         {item.driver_name}
//                       </option>
//                     ))}
//                   </Select>
//                   {reassignData.driver_error && (
//                     <Text
//                       color="red.500"
//                       fontSize="sm"
//                       mt={1}
//                       display="flex"
//                       alignItems="center"
//                     >
//                       ⚠️ {reassignData.driver_error}
//                     </Text>
//                   )}
//                 </Box>
//               </FormControl>

//               {/* Customer Information */}
//               <Box
//                 p={4}
//                 bg={infoCardBg}
//                 borderRadius="lg"
//                 border="1px solid"
//                 borderColor="blue.200"
//               >
//                 <Text fontSize="sm" fontWeight="bold" color="blue.700" mb={2}>
//                   Customer Information
//                 </Text>
//                 <Text fontSize="xs" mb={1}>
//                   Name: {selectedOrder?.customer_name || "Unknown"}
//                 </Text>
//                 <Text fontSize="xs">
//                   Address:{" "}
//                   {selectedOrder?.shipping_address ||
//                     selectedOrder?.address_id ||
//                     "N/A"}
//                 </Text>
//               </Box>
//             </Flex>
//           </ModalBody>

//           <ModalFooter
//             display="flex"
//             w="full"
//             gap={3}
//             justifyContent="flex-end"
//             borderTop="1px solid"
//             borderColor={borderColor}
//             pt={4}
//           >
//             <Button
//               colorScheme="gray"
//               onClick={() => {
//                 onCloseReassign();
//                 setReassignData({
//                   order_id: "",
//                   dplist: "",
//                   vehicle: "",
//                   driver: "",
//                   dplist_error: "",
//                   vehicle_error: "",
//                   driver_error: "",
//                 });
//                 setSelectedOrder(null);
//               }}
//               variant="outline"
//               size="md"
//             >
//               Cancel
//             </Button>
//             <Button
//               colorScheme="blue"
//               onClick={handleReassignSubmit}
//               isLoading={loading}
//               size="md"
//               px={6}
//               leftIcon={<MdLocalShipping />}
//             >
//               Reassign Order
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//     </Box>
//   );
// };

// export default VehicleOrderMap;
// // import React, { useEffect, useState } from "react";
// // import {
// //   Box,
// //   Flex,
// //   Heading,
// //   Text,
// //   VStack,
// //   Badge,
// //   useColorModeValue,
// //   Spinner,
// //   useToast,
// //   HStack,
// //   Divider,
// // } from "@chakra-ui/react";
// // import {
// //   GoogleMap,
// //   useLoadScript,
// //   Marker,
// //   InfoWindow,
// // } from "@react-google-maps/api";
// // import { MdLocationOn } from "react-icons/md";
// // import { baseUrl } from "../../utils/utils";
// // import CustomButton from "../../components/CustomButton";

// // const VehicleOrderMap = () => {
// //   const toast = useToast();

// //   // ALL HOOKS MUST BE AT THE TOP BEFORE ANY CONDITIONAL RETURNS
// //   const [map, setMap] = useState(null);
// //   const [loading, setLoading] = useState(false);
// //   const [activeMarker, setActiveMarker] = useState(null);
// //   const [vehiclesData, setVehiclesData] = useState([]);

// //   // Load Google Maps Script
// //   const { isLoaded, loadError } = useLoadScript({
// //     googleMapsApiKey: "AIzaSyDF2pgkAq9rIaeQGY7HL89D6fZe70XIY2I",
// //   });

// //   // Color mode values
// //   const cardBg = useColorModeValue("white", "gray.800");
// //   const borderColor = useColorModeValue("gray.200", "gray.600");
// //   const textColor = useColorModeValue("gray.600", "gray.400");
// //   const headingColor = useColorModeValue("gray.700", "gray.200");
// //   const bgColor = useColorModeValue("gray.50", "gray.900");

// //   // Map container style - Full screen
// //   const mapContainerStyle = {
// //     width: "100%",
// //     height: "100%",
// //   };

// //   // Default center (will be updated based on data)
// //   const [center, setCenter] = useState({
// //     lat: 22.718435,
// //     lng: 75.855217,
// //   });

// //   // Google Maps options
// //   const options = {
// //     disableDefaultUI: false,
// //     zoomControl: true,
// //     streetViewControl: false,
// //     mapTypeControl: false,
// //     fullscreenControl: true,
// //   };

// //   // Fetch live tracking data
// //   const fetchLiveTracking = async () => {
// //     try {
// //       setLoading(true);
// //       const body = new FormData();
// //       body.append("action", "live_tracking");

// //       const response = await fetch(`${baseUrl}orders.php`, {
// //         method: "POST",
// //         body: body,
// //       });

// //       const data = await response.json();

// //       if (data.success) {
// //         setVehiclesData(data.data);

// //         // Set center to first vehicle location if available
// //         if (data.data.length > 0 && data.data[0].vehicle_latlong) {
// //           const [lat, lng] = data.data[0].vehicle_latlong
// //             .split(",")
// //             .map(parseFloat);
// //           if (!isNaN(lat) && !isNaN(lng)) {
// //             setCenter({ lat, lng });
// //           }
// //         }
// //       } else {
// //         toast({
// //           title: "Error",
// //           description: data.message || "Failed to fetch tracking data",
// //           status: "error",
// //           duration: 3000,
// //           isClosable: true,
// //         });
// //       }
// //     } catch (error) {
// //       console.error("Error fetching live tracking:", error);
// //       toast({
// //         title: "Error",
// //         description: "Failed to fetch live tracking data",
// //         status: "error",
// //         duration: 3000,
// //         isClosable: true,
// //       });
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   useEffect(() => {
// //     fetchLiveTracking();
// //   }, []);

// //   // Handle map load
// //   const onLoad = (map) => {
// //     setMap(map);
// //   };

// //   // Get customer location icon (Red Pin)
// //   const getCustomerIcon = () => {
// //     if (!isLoaded || !window.google) return null;

// //     return {
// //       url:
// //         "data:image/svg+xml;charset=UTF-8," +
// //         encodeURIComponent(`
// //         <svg xmlns="http://www.w3.org/2000/svg" width="35" height="45" viewBox="0 0 35 45">
// //           <path d="M17.5 0 C8.75 0 1.75 7 1.75 15.75 C1.75 24.5 17.5 45 17.5 45 C17.5 45 33.25 24.5 33.25 15.75 C33.25 7 26.25 0 17.5 0 Z" fill="#dc3545" stroke="white" stroke-width="2"/>
// //           <circle cx="17.5" cy="15.75" r="7" fill="white"/>
// //         </svg>
// //       `),
// //       scaledSize: new window.google.maps.Size(35, 45),
// //     };
// //   };

// //   // Get DP/Vehicle location icon (Blue Truck)
// //   const getVehicleIcon = () => {
// //     if (!isLoaded || !window.google) return null;

// //     return {
// //       url:
// //         "data:image/svg+xml;charset=UTF-8," +
// //         encodeURIComponent(`
// //       <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 45 45">
// //         <circle cx="22.5" cy="22.5" r="20" fill="#0175c2" stroke="white" stroke-width="3"/>
// //         <g transform="translate(11, 14)" fill="white">
// //           <rect x="0" y="4" width="12" height="7" rx="1"/>
// //           <path d="M12 4 L12 8 L16 8 L16 11 L12 11 L12 11" />
// //           <path d="M13 6 L15 8 L13 10 Z" />
// //           <circle cx="4" cy="12" r="1.5"/>
// //           <circle cx="14" cy="12" r="1.5"/>
// //           <rect x="1" y="2" width="7" height="2"/>
// //         </g>
// //       </svg>
// //     `),
// //       scaledSize: new window.google.maps.Size(45, 45),
// //     };
// //   };

// //   // Generate unique colors for each vehicle
// //   const getVehicleColor = (index) => {
// //     const colors = [
// //       "#0175c2", // Blue
// //       "#28a745", // Green
// //       "#ffc107", // Yellow
// //       "#dc3545", // Red
// //       "#6f42c1", // Purple
// //       "#fd7e14", // Orange
// //       "#20c997", // Teal
// //       "#e83e8c", // Pink
// //     ];
// //     return colors[index % colors.length];
// //   };

// //   // Get colored customer icon based on vehicle
// //   const getCustomerIconColored = (color) => {
// //     if (!isLoaded || !window.google) return null;

// //     return {
// //       url:
// //         "data:image/svg+xml;charset=UTF-8," +
// //         encodeURIComponent(`
// //         <svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
// //           <path d="M15 0 C7.5 0 1.5 6 1.5 13.5 C1.5 21 15 40 15 40 C15 40 28.5 21 28.5 13.5 C28.5 6 22.5 0 15 0 Z" fill="${color}" stroke="white" stroke-width="2"/>
// //           <circle cx="15" cy="13.5" r="6" fill="white"/>
// //         </svg>
// //       `),
// //       scaledSize: new window.google.maps.Size(30, 40),
// //     };
// //   };

// //   // Get colored vehicle icon
// //   const getVehicleIconColored = (color) => {
// //     if (!isLoaded || !window.google) return null;

// //     return {
// //       url:
// //         "data:image/svg+xml;charset=UTF-8," +
// //         encodeURIComponent(`
// //       <svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 45 45">
// //         <circle cx="22.5" cy="22.5" r="20" fill="${color}" stroke="white" stroke-width="3"/>
// //         <g transform="translate(11, 14)" fill="white">
// //           <rect x="0" y="4" width="12" height="7" rx="1"/>
// //           <path d="M12 4 L12 8 L16 8 L16 11 L12 11 L12 11" />
// //           <path d="M13 6 L15 8 L13 10 Z" />
// //           <circle cx="4" cy="12" r="1.5"/>
// //           <circle cx="14" cy="12" r="1.5"/>
// //           <rect x="1" y="2" width="7" height="2"/>
// //         </g>
// //       </svg>
// //     `),
// //       scaledSize: new window.google.maps.Size(45, 45),
// //     };
// //   };

// //   // CONDITIONAL RETURNS AFTER ALL HOOKS
// //   if (loadError) {
// //     return (
// //       <Box p={4} textAlign="center">
// //         <Heading size="md" color="red.500" mb={2}>
// //           Error Loading Maps
// //         </Heading>
// //         <Text color={textColor}>
// //           Failed to load Google Maps. Please check your API key and internet
// //           connection.
// //         </Text>
// //       </Box>
// //     );
// //   }

// //   if (!isLoaded) {
// //     return (
// //       <Flex justify="center" align="center" minH="100vh">
// //         <VStack>
// //           <Spinner size="xl" color="#0175c2" thickness="4px" />
// //           <Text mt={4} color={textColor}>
// //             Loading Google Maps...
// //           </Text>
// //         </VStack>
// //       </Flex>
// //     );
// //   }

// //   return (
// //     <Box position="relative" w="100%" h="100vh">
// //       {/* Map */}
// //       {loading && vehiclesData.length === 0 ? (
// //         <Flex justify="center" align="center" h="100vh" bg={bgColor}>
// //           <VStack>
// //             <Spinner size="xl" color="#0175c2" thickness="4px" />
// //             <Text mt={4} color={textColor}>
// //               Loading tracking data...
// //             </Text>
// //           </VStack>
// //         </Flex>
// //       ) : vehiclesData.length === 0 ? (
// //         <Flex justify="center" align="center" h="100vh" bg={bgColor}>
// //           <VStack>
// //             <MdLocationOn size={64} color="#0175c2" />
// //             <Heading size="md" color={headingColor} mt={4}>
// //               No Vehicles Found
// //             </Heading>
// //             <Text color={textColor}>
// //               There are no vehicles with location data to display
// //             </Text>
// //           </VStack>
// //         </Flex>
// //       ) : (
// //         <>
// //           <GoogleMap
// //             mapContainerStyle={mapContainerStyle}
// //             center={center}
// //             zoom={12}
// //             options={options}
// //             onLoad={onLoad}
// //           >
// //             {/* Loop through each vehicle */}
// //             {vehiclesData.map((vehicle, vehicleIndex) => {
// //               const vehicleColor = getVehicleColor(vehicleIndex);

// //               // Parse vehicle location
// //               if (!vehicle.vehicle_latlong) return null;
// //               const [vLat, vLng] = vehicle.vehicle_latlong
// //                 .split(",")
// //                 .map(parseFloat);
// //               if (isNaN(vLat) || isNaN(vLng)) return null;

// //               return (
// //                 <React.Fragment key={`vehicle-group-${vehicleIndex}`}>
// //                   {/* Vehicle Marker */}
// //                   <Marker
// //                     position={{ lat: vLat, lng: vLng }}
// //                     icon={getVehicleIconColored(vehicleColor)}
// //                     onClick={() =>
// //                       setActiveMarker(`vehicle-${vehicle.vehicle_id}`)
// //                     }
// //                   >
// //                     {activeMarker === `vehicle-${vehicle.vehicle_id}` && (
// //                       <InfoWindow onCloseClick={() => setActiveMarker(null)}>
// //                         <Box p={2} maxW="250px">
// //                           <Flex align="center" gap={2} mb={2}>
// //                             <Badge
// //                               bg={vehicleColor}
// //                               color="white"
// //                               fontSize="10px"
// //                             >
// //                               Vehicle
// //                             </Badge>
// //                           </Flex>
// //                           <Heading size="xs" mb={1} color={vehicleColor}>
// //                             {vehicle.vehicleno}
// //                           </Heading>
// //                           <Text fontSize="xs" mb={1}>
// //                             Driver: {vehicle.driver_name || "Not Assigned"}
// //                           </Text>
// //                           <Text fontSize="xs" mb={1}>
// //                             DP:{" "}
// //                             {vehicle.delivery_partner_name || "Not Assigned"}
// //                           </Text>
// //                           <Text fontSize="9px" color="gray.500">
// //                             Updated: {vehicle.last_updated}
// //                           </Text>
// //                           <Badge colorScheme="green" fontSize="9px" mt={1}>
// //                             {vehicle.customers?.length || 0} Customer(s)
// //                           </Badge>
// //                         </Box>
// //                       </InfoWindow>
// //                     )}
// //                   </Marker>

// //                   {/* Customer Markers for this vehicle */}
// //                   {vehicle.customers?.map((customer, customerIndex) => {
// //                     // Parse customer location
// //                     if (!customer.customer_latlong) return null;
// //                     const [cLat, cLng] = customer.customer_latlong
// //                       .split(",")
// //                       .map(parseFloat);
// //                     if (isNaN(cLat) || isNaN(cLng)) return null;

// //                     return (
// //                       <Marker
// //                         key={`customer-${vehicle.vehicle_id}-${customerIndex}`}
// //                         position={{ lat: cLat, lng: cLng }}
// //                         icon={getCustomerIconColored(vehicleColor)}
// //                         onClick={() =>
// //                           setActiveMarker(
// //                             `customer-${vehicle.vehicle_id}-${customerIndex}`,
// //                           )
// //                         }
// //                       >
// //                         {activeMarker ===
// //                           `customer-${vehicle.vehicle_id}-${customerIndex}` && (
// //                           <InfoWindow
// //                             onCloseClick={() => setActiveMarker(null)}
// //                           >
// //                             <Box p={2} maxW="250px">
// //                               <Flex align="center" gap={2} mb={2}>
// //                                 <Badge
// //                                   bg={vehicleColor}
// //                                   color="white"
// //                                   fontSize="10px"
// //                                 >
// //                                   Customer
// //                                 </Badge>
// //                               </Flex>
// //                               <Heading size="xs" mb={1} color={vehicleColor}>
// //                                 Order: {customer.orderid}
// //                               </Heading>
// //                               <Text fontSize="xs" mb={1} fontWeight="bold">
// //                                 {customer.customer_name || "Unknown Customer"}
// //                               </Text>
// //                               <Text
// //                                 fontSize="xs"
// //                                 mb={1}
// //                                 color="gray.600"
// //                                 noOfLines={3}
// //                               >
// //                                 {customer.shipping_address ||
// //                                   customer.address_id ||
// //                                   "Address not available"}
// //                               </Text>
// //                               <Divider my={1} />
// //                               <Text fontSize="9px" color="gray.500">
// //                                 Assigned Vehicle: {vehicle.vehicleno}
// //                               </Text>
// //                               <HStack mt={2}>
// //                                 <CustomButton title={"Re-Assign"} />
// //                               </HStack>
// //                             </Box>
// //                           </InfoWindow>
// //                         )}
// //                       </Marker>
// //                     );
// //                   })}
// //                 </React.Fragment>
// //               );
// //             })}
// //           </GoogleMap>

// //           {/* Legend Overlay on Map */}
// //           <Box
// //             position="absolute"
// //             bottom="20px"
// //             left="50%"
// //             transform="translateX(-50%)"
// //             bg="white"
// //             px={6}
// //             py={3}
// //             borderRadius="lg"
// //             boxShadow="lg"
// //             border="1px solid"
// //             borderColor={borderColor}
// //             zIndex={1000}
// //           >
// //             <Heading size="sm" mb={2} color={headingColor}>
// //               Map Legend
// //             </Heading>
// //             <HStack spacing={6}>
// //               <Flex align="center" gap={2}>
// //                 <Box
// //                   w="24px"
// //                   h="24px"
// //                   bg="#0175c2"
// //                   borderRadius="full"
// //                   border="2px solid white"
// //                   boxShadow="sm"
// //                 />
// //                 <Text fontSize="sm" color={textColor} fontWeight="medium">
// //                   Vehicle Location
// //                 </Text>
// //               </Flex>
// //               <Flex align="center" gap={2}>
// //                 <Box
// //                   w="20px"
// //                   h="26px"
// //                   bg="#0175c2"
// //                   clipPath="polygon(50% 0%, 100% 38%, 100% 100%, 0% 100%, 0% 38%)"
// //                   border="2px solid white"
// //                   boxShadow="sm"
// //                 />
// //                 <Text fontSize="sm" color={textColor} fontWeight="medium">
// //                   Customer Location
// //                 </Text>
// //               </Flex>
// //             </HStack>
// //             <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">
// //               Each color represents a different vehicle and its assigned
// //               customers
// //             </Text>
// //           </Box>
// //         </>
// //       )}
// //     </Box>
// //   );
// // };

// // export default VehicleOrderMap;
// // // import React, { useEffect, useState } from "react";
// // // import {
// // //   Box,
// // //   Flex,
// // //   Heading,
// // //   Text,
// // //   VStack,
// // //   Badge,
// // //   useColorModeValue,
// // //   Spinner,
// // //   Select,
// // //   FormControl,
// // //   FormLabel,
// // //   Card,
// // //   CardBody,
// // //   Divider,
// // //   HStack,
// // //   useToast,
// // // } from "@chakra-ui/react";
// // // import {
// // //   GoogleMap,
// // //   useLoadScript,
// // //   Marker,
// // //   InfoWindow,
// // //   DirectionsRenderer,
// // // } from "@react-google-maps/api";
// // // import { FiTruck, FiUser, FiMapPin } from "react-icons/fi";
// // // import { MdLocalShipping, MdLocationOn } from "react-icons/md";
// // // import { ArrowLeft } from "lucide-react";
// // // import { useNavigate } from "react-router-dom";
// // // import { baseUrl } from "../../utils/utils";

// // // const VehicleOrderMap = () => {
// // //   const navigate = useNavigate();
// // //   const toast = useToast();

// // //   // ALL HOOKS MUST BE AT THE TOP BEFORE ANY CONDITIONAL RETURNS
// // //   const [map, setMap] = useState(null);
// // //   const [selectedVehicle, setSelectedVehicle] = useState(null);
// // //   const [selectedOrder, setSelectedOrder] = useState(null);
// // //   const [directions, setDirections] = useState(null);
// // //   const [loading, setLoading] = useState(false);
// // //   const [activeMarker, setActiveMarker] = useState(null);
// // //   const [orders, setOrders] = useState([]);
// // //   const [vehicles, setVehicles] = useState([]);

// // //   // Load Google Maps Script
// // //   const { isLoaded, loadError } = useLoadScript({
// // //     googleMapsApiKey: "AIzaSyDF2pgkAq9rIaeQGY7HL89D6fZe70XIY2I", // Replace with your API key
// // //   });

// // //   // Color mode values
// // //   const cardBg = useColorModeValue("white", "gray.800");
// // //   const borderColor = useColorModeValue("gray.200", "gray.600");
// // //   const textColor = useColorModeValue("gray.600", "gray.400");
// // //   const headingColor = useColorModeValue("gray.700", "gray.200");
// // //   const bgColor = useColorModeValue("gray.50", "gray.900");

// // //   // Map container style
// // //   const mapContainerStyle = {
// // //     width: "100%",
// // //     height: "600px",
// // //     borderRadius: "12px",
// // //   };

// // //   // Default center (will be updated based on data)
// // //   const [center, setCenter] = useState({
// // //     lat: 17.4406853,
// // //     lng: 78.4466092,
// // //   });

// // //   // Google Maps options
// // //   const options = {
// // //     disableDefaultUI: false,
// // //     zoomControl: true,
// // //     streetViewControl: false,
// // //     mapTypeControl: false,
// // //     fullscreenControl: true,
// // //   };

// // //   // Fetch live tracking data
// // //   const fetchLiveTracking = async () => {
// // //     try {
// // //       setLoading(true);
// // //       const body = new FormData();
// // //       body.append("action", "live_tracking");

// // //       const response = await fetch(`${baseUrl}orders.php`, {
// // //         method: "POST",
// // //         body: body,
// // //       });

// // //       const data = await response.json();

// // //       if (data.success) {
// // //         // Parse orders and extract location data
// // //         const parsedOrders = data.data
// // //           .map((order) => {
// // //             if (!order.latlong) return null;

// // //             const [lat, lng] = order.latlong.split(",").map(parseFloat);

// // //             if (isNaN(lat) || isNaN(lng)) return null;

// // //             return {
// // //               id: order.id,
// // //               orderNo: `ORD-${order.id}`,
// // //               customerName: order.customer_name || "Unknown Customer",
// // //               address: order.shipping_address || "Address not available",
// // //               location: { lat, lng },
// // //               status: "Pending",
// // //               addressId: order.address_id,
// // //             };
// // //           })
// // //           .filter(Boolean); // Remove null values

// // //         setOrders(parsedOrders);

// // //         // Set center to first order location if available
// // //         if (parsedOrders.length > 0) {
// // //           setCenter(parsedOrders[0].location);
// // //         }

// // //         // For now, using sample vehicles - you can add vehicle API later
// // //         setVehicles([
// // //           {
// // //             id: 1,
// // //             vehicleNo: "MP09RD3418",
// // //             driverName: "Driver 1",
// // //             status: "Active",
// // //             location: parsedOrders[0]?.location || {
// // //               lat: 17.4406853,
// // //               lng: 78.4466092,
// // //             },
// // //           },
// // //         ]);
// // //       } else {
// // //         toast({
// // //           title: "Error",
// // //           description: data.message || "Failed to fetch tracking data",
// // //           status: "error",
// // //           duration: 3000,
// // //           isClosable: true,
// // //         });
// // //       }
// // //     } catch (error) {
// // //       console.error("Error fetching live tracking:", error);
// // //       toast({
// // //         title: "Error",
// // //         description: "Failed to fetch live tracking data",
// // //         status: "error",
// // //         duration: 3000,
// // //         isClosable: true,
// // //       });
// // //     } finally {
// // //       setLoading(false);
// // //     }
// // //   };

// // //   useEffect(() => {
// // //     fetchLiveTracking();
// // //   }, []);

// // //   // Handle map load
// // //   const onLoad = (map) => {
// // //     setMap(map);
// // //   };

// // //   // Calculate route between vehicle and order
// // //   const calculateRoute = (vehicleId, orderId) => {
// // //     if (!isLoaded) return;

// // //     const vehicle = vehicles.find((v) => v.id === vehicleId);
// // //     const order = orders.find((o) => o.id === orderId);

// // //     if (!vehicle || !order) return;

// // //     setLoading(true);

// // //     const directionsService = new window.google.maps.DirectionsService();

// // //     directionsService.route(
// // //       {
// // //         origin: vehicle.location,
// // //         destination: order.location,
// // //         travelMode: window.google.maps.TravelMode.DRIVING,
// // //       },
// // //       (result, status) => {
// // //         setLoading(false);
// // //         if (status === window.google.maps.DirectionsStatus.OK) {
// // //           setDirections(result);
// // //           setSelectedVehicle(vehicleId);
// // //           setSelectedOrder(orderId);
// // //         } else {
// // //           console.error("Directions request failed:", status);
// // //           toast({
// // //             title: "Error",
// // //             description: "Failed to calculate route",
// // //             status: "error",
// // //             duration: 3000,
// // //             isClosable: true,
// // //           });
// // //         }
// // //       },
// // //     );
// // //   };

// // //   // Handle vehicle selection
// // //   const handleVehicleSelect = (e) => {
// // //     const vehicleId = parseInt(e.target.value);
// // //     setSelectedVehicle(vehicleId);
// // //     setDirections(null);

// // //     // Center map on selected vehicle
// // //     const vehicle = vehicles.find((v) => v.id === vehicleId);
// // //     if (vehicle && map) {
// // //       map.panTo(vehicle.location);
// // //       map.setZoom(13);
// // //     }
// // //   };

// // //   // Handle order selection
// // //   const handleOrderSelect = (e) => {
// // //     const orderId = e.target.value;
// // //     setSelectedOrder(orderId);

// // //     // If both vehicle and order are selected, calculate route
// // //     if (selectedVehicle && orderId) {
// // //       calculateRoute(selectedVehicle, orderId);
// // //     }

// // //     // Center map on selected order
// // //     const order = orders.find((o) => o.id === orderId);
// // //     if (order && map) {
// // //       map.panTo(order.location);
// // //       map.setZoom(13);
// // //     }
// // //   };

// // //   // Get vehicle icon URL
// // //   const getVehicleIcon = () => {
// // //     if (!isLoaded || !window.google) return null;

// // //     return {
// // //       url:
// // //         "data:image/svg+xml;charset=UTF-8," +
// // //         encodeURIComponent(`
// // //         <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
// // //           <circle cx="20" cy="20" r="18" fill="#0175c2" stroke="white" stroke-width="3"/>
// // //           <path d="M20 10 L28 25 L20 22 L12 25 Z" fill="white"/>
// // //         </svg>
// // //       `),
// // //       scaledSize: new window.google.maps.Size(40, 40),
// // //     };
// // //   };

// // //   // Get order icon URL
// // //   const getOrderIcon = () => {
// // //     if (!isLoaded || !window.google) return null;

// // //     return {
// // //       url:
// // //         "data:image/svg+xml;charset=UTF-8," +
// // //         encodeURIComponent(`
// // //         <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
// // //           <path d="M20 0 C10 0 2 8 2 18 C2 28 20 50 20 50 C20 50 38 28 38 18 C38 8 30 0 20 0 Z" fill="#dc3545" stroke="white" stroke-width="2"/>
// // //           <circle cx="20" cy="18" r="8" fill="white"/>
// // //         </svg>
// // //       `),
// // //       scaledSize: new window.google.maps.Size(40, 50),
// // //     };
// // //   };

// // //   // CONDITIONAL RETURNS AFTER ALL HOOKS
// // //   if (loadError) {
// // //     return (
// // //       <Box p={4} textAlign="center">
// // //         <Heading size="md" color="red.500" mb={2}>
// // //           Error Loading Maps
// // //         </Heading>
// // //         <Text color={textColor}>
// // //           Failed to load Google Maps. Please check your API key and internet
// // //           connection.
// // //         </Text>
// // //       </Box>
// // //     );
// // //   }

// // //   if (!isLoaded) {
// // //     return (
// // //       <Flex justify="center" align="center" minH="100vh">
// // //         <VStack>
// // //           <Spinner size="xl" color="#0175c2" thickness="4px" />
// // //           <Text mt={4} color={textColor}>
// // //             Loading Google Maps...
// // //           </Text>
// // //         </VStack>
// // //       </Flex>
// // //     );
// // //   }

// // //   return (
// // //     <Box p={4} minH="100vh" bg={bgColor}>
// // //       <Box maxW="1400px" mx="auto">
// // //         {/* Header */}
// // //         {/* <Flex justify="space-between" align="center" mb={4}>
// // //           <Box>
// // //             <button
// // //               onClick={() => navigate(-1)}
// // //               style={{
// // //                 display: "flex",
// // //                 alignItems: "center",
// // //                 gap: 5,
// // //                 color: "#403e3e",
// // //                 marginBottom: 8,
// // //               }}
// // //             >
// // //               <ArrowLeft />
// // //               <p style={{ fontSize: 17, fontWeight: "bold" }}>Back</p>
// // //             </button>
// // //             <Heading size="lg" mb={1} color="#0175c2">
// // //               Live Order Tracking
// // //             </Heading>
// // //             <Text color={textColor} fontSize="sm">
// // //               Track vehicle locations and customer order addresses in real-time
// // //             </Text>
// // //           </Box>
// // //         </Flex> */}

// // //         {/* Stats */}
// // //         {/* <Flex gap={3} mb={4}>
// // //           <Card flex={1} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3}>
// // //               <Flex align="center" justify="space-between">
// // //                 <Box>
// // //                   <Text fontSize="xs" color={textColor} mb={1}>
// // //                     Active Vehicles
// // //                   </Text>
// // //                   <Heading size="md" color="#0175c2">
// // //                     {vehicles.filter((v) => v.status === "Active").length}
// // //                   </Heading>
// // //                 </Box>
// // //                 <Box bg="#E6F4FA" p={2} borderRadius="md">
// // //                   <FiTruck size={20} color="#0175c2" />
// // //                 </Box>
// // //               </Flex>
// // //             </CardBody>
// // //           </Card>

// // //           <Card flex={1} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3}>
// // //               <Flex align="center" justify="space-between">
// // //                 <Box>
// // //                   <Text fontSize="xs" color={textColor} mb={1}>
// // //                     Pending Orders
// // //                   </Text>
// // //                   <Heading size="md" color="#0175c2">
// // //                     {orders.filter((o) => o.status === "Pending").length}
// // //                   </Heading>
// // //                 </Box>
// // //                 <Box bg="#E6F4FA" p={2} borderRadius="md">
// // //                   <MdLocalShipping size={20} color="#0175c2" />
// // //                 </Box>
// // //               </Flex>
// // //             </CardBody>
// // //           </Card>

// // //           <Card flex={1} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3}>
// // //               <Flex align="center" justify="space-between">
// // //                 <Box>
// // //                   <Text fontSize="xs" color={textColor} mb={1}>
// // //                     Total Orders
// // //                   </Text>
// // //                   <Heading size="md" color="#0175c2">
// // //                     {orders.length}
// // //                   </Heading>
// // //                 </Box>
// // //                 <Box bg="#E6F4FA" p={2} borderRadius="md">
// // //                   <MdLocationOn size={20} color="#0175c2" />
// // //                 </Box>
// // //               </Flex>
// // //             </CardBody>
// // //           </Card>
// // //         </Flex> */}

// // //         {/* Filters */}
// // //         {/* <Card bg={cardBg} mb={4} borderColor={borderColor}>
// // //           <CardBody p={3}>
// // //             <Flex gap={3}>
// // //               <FormControl flex={1}>
// // //                 <FormLabel fontSize="sm" mb={1} color={headingColor}>
// // //                   Select Vehicle
// // //                 </FormLabel>
// // //                 <Select
// // //                   size="sm"
// // //                   placeholder="Choose a vehicle"
// // //                   onChange={handleVehicleSelect}
// // //                   value={selectedVehicle || ""}
// // //                   _focus={{
// // //                     borderColor: "#0175c2",
// // //                     boxShadow: "0 0 0 1px #0175c2",
// // //                   }}
// // //                 >
// // //                   {vehicles.map((vehicle) => (
// // //                     <option key={vehicle.id} value={vehicle.id}>
// // //                       {vehicle.vehicleNo} - {vehicle.driverName}
// // //                     </option>
// // //                   ))}
// // //                 </Select>
// // //               </FormControl>

// // //               <FormControl flex={1}>
// // //                 <FormLabel fontSize="sm" mb={1} color={headingColor}>
// // //                   Select Order
// // //                 </FormLabel>
// // //                 <Select
// // //                   size="sm"
// // //                   placeholder="Choose an order"
// // //                   onChange={handleOrderSelect}
// // //                   value={selectedOrder || ""}
// // //                   disabled={!selectedVehicle}
// // //                   _focus={{
// // //                     borderColor: "#0175c2",
// // //                     boxShadow: "0 0 0 1px #0175c2",
// // //                   }}
// // //                 >
// // //                   {orders.map((order) => (
// // //                     <option key={order.id} value={order.id}>
// // //                       {order.orderNo} - {order.customerName}
// // //                     </option>
// // //                   ))}
// // //                 </Select>
// // //               </FormControl>
// // //             </Flex>
// // //           </CardBody>
// // //         </Card> */}

// // //         {/* Map and Details */}
// // //         <Flex gap={3}>
// // //           {/* Map */}
// // //           <Card flex={2} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3} position="relative">
// // //               {loading && orders.length === 0 ? (
// // //                 <Flex justify="center" align="center" h="600px">
// // //                   <VStack>
// // //                     <Spinner size="xl" color="#0175c2" thickness="4px" />
// // //                     <Text mt={4} color={textColor}>
// // //                       Loading tracking data...
// // //                     </Text>
// // //                   </VStack>
// // //                 </Flex>
// // //               ) : orders.length === 0 ? (
// // //                 <Flex justify="center" align="center" h="600px">
// // //                   <VStack>
// // //                     <MdLocationOn size={64} color="#0175c2" />
// // //                     <Heading size="md" color={headingColor} mt={4}>
// // //                       No Orders Found
// // //                     </Heading>
// // //                     <Text color={textColor}>
// // //                       There are no orders with location data to display
// // //                     </Text>
// // //                   </VStack>
// // //                 </Flex>
// // //               ) : (
// // //                 <GoogleMap
// // //                   mapContainerStyle={mapContainerStyle}
// // //                   center={center}
// // //                   zoom={12}
// // //                   options={options}
// // //                   onLoad={onLoad}
// // //                 >
// // //                   {/* Vehicle Markers */}
// // //                   {vehicles.map((vehicle) => (
// // //                     <Marker
// // //                       key={`vehicle-${vehicle.id}`}
// // //                       position={vehicle.location}
// // //                       icon={getVehicleIcon()}
// // //                       onClick={() => setActiveMarker(`vehicle-${vehicle.id}`)}
// // //                     >
// // //                       {activeMarker === `vehicle-${vehicle.id}` && (
// // //                         <InfoWindow onCloseClick={() => setActiveMarker(null)}>
// // //                           <Box p={2}>
// // //                             <Heading size="xs" mb={1} color="#0175c2">
// // //                               {vehicle.vehicleNo}
// // //                             </Heading>
// // //                             <Text fontSize="xs" mb={1}>
// // //                               Driver: {vehicle.driverName}
// // //                             </Text>
// // //                             <Badge colorScheme="green" fontSize="9px">
// // //                               {vehicle.status}
// // //                             </Badge>
// // //                           </Box>
// // //                         </InfoWindow>
// // //                       )}
// // //                     </Marker>
// // //                   ))}

// // //                   {/* Order Markers */}
// // //                   {orders.map((order) => (
// // //                     <Marker
// // //                       key={`order-${order.id}`}
// // //                       position={order.location}
// // //                       icon={getOrderIcon()}
// // //                       onClick={() => setActiveMarker(`order-${order.id}`)}
// // //                     >
// // //                       {activeMarker === `order-${order.id}` && (
// // //                         <InfoWindow onCloseClick={() => setActiveMarker(null)}>
// // //                           <Box p={2}>
// // //                             <Heading size="xs" mb={1} color="#dc3545">
// // //                               {order.orderNo}
// // //                             </Heading>
// // //                             <Text fontSize="xs" mb={1}>
// // //                               {order.customerName}
// // //                             </Text>
// // //                             <Text
// // //                               fontSize="xs"
// // //                               mb={1}
// // //                               color="gray.600"
// // //                               noOfLines={2}
// // //                             >
// // //                               {order.address}
// // //                             </Text>
// // //                             <Badge colorScheme="orange" fontSize="9px">
// // //                               {order.status}
// // //                             </Badge>
// // //                           </Box>
// // //                         </InfoWindow>
// // //                       )}
// // //                     </Marker>
// // //                   ))}

// // //                   {/* Directions */}
// // //                   {directions && (
// // //                     <DirectionsRenderer
// // //                       directions={directions}
// // //                       options={{
// // //                         polylineOptions: {
// // //                           strokeColor: "#0175c2",
// // //                           strokeWeight: 5,
// // //                         },
// // //                       }}
// // //                     />
// // //                   )}
// // //                 </GoogleMap>
// // //               )}

// // //               {loading && orders.length > 0 && (
// // //                 <Flex
// // //                   position="absolute"
// // //                   top="50%"
// // //                   left="50%"
// // //                   transform="translate(-50%, -50%)"
// // //                   bg="whiteAlpha.900"
// // //                   p={4}
// // //                   borderRadius="md"
// // //                   zIndex={10}
// // //                 >
// // //                   <Spinner color="#0175c2" />
// // //                   <Text ml={3}>Calculating route...</Text>
// // //                 </Flex>
// // //               )}
// // //             </CardBody>
// // //           </Card>

// // //           {/* Details Panel */}
// // //           {/* <VStack flex={1} spacing={3} align="stretch">
// // //             {/* Selected Vehicle Details *
// // //             {selectedVehicle && (
// // //               <Card bg={cardBg} borderColor={borderColor}>
// // //                 <CardBody p={3}>
// // //                   <Flex align="center" gap={2} mb={2}>
// // //                     <Box bg="#0175c2" p={2} borderRadius="md" color="white">
// // //                       <FiTruck size={16} />
// // //                     </Box>
// // //                     <Heading size="sm" color={headingColor}>
// // //                       Vehicle Details
// // //                     </Heading>
// // //                   </Flex>
// // //                   <Divider mb={2} />
// // //                   {(() => {
// // //                     const vehicle = vehicles.find(
// // //                       (v) => v.id === selectedVehicle,
// // //                     );
// // //                     return vehicle ? (
// // //                       <VStack align="stretch" spacing={2}>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Vehicle Number
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {vehicle.vehicleNo}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Driver Name
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {vehicle.driverName}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Status
// // //                           </Text>
// // //                           <Badge colorScheme="green" fontSize="9px">
// // //                             {vehicle.status}
// // //                           </Badge>
// // //                         </Box>
// // //                       </VStack>
// // //                     ) : null;
// // //                   })()}
// // //                 </CardBody>
// // //               </Card>
// // //             )}

// // //             {/* Selected Order Details */}
// // //           {/* {selectedOrder && (
// // //               <Card bg={cardBg} borderColor={borderColor}>
// // //                 <CardBody p={3}>
// // //                   <Flex align="center" gap={2} mb={2}>
// // //                     <Box bg="#dc3545" p={2} borderRadius="md" color="white">
// // //                       <MdLocationOn size={16} />
// // //                     </Box>
// // //                     <Heading size="sm" color={headingColor}>
// // //                       Order Details
// // //                     </Heading>
// // //                   </Flex>
// // //                   <Divider mb={2} />
// // //                   {(() => {
// // //                     const order = orders.find((o) => o.id === selectedOrder);
// // //                     return order ? (
// // //                       <VStack align="stretch" spacing={2}>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Order Number
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.orderNo}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Customer Name
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.customerName}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Delivery Address
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.address}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Status
// // //                           </Text>
// // //                           <Badge colorScheme="orange" fontSize="9px">
// // //                             {order.status}
// // //                           </Badge>
// // //                         </Box>
// // //                       </VStack>
// // //                     ) : null;
// // //                   })()}
// // //                 </CardBody>
// // //               </Card>
// // //             )} */}

// // //           {/* Legend */}
// // //           {/* <Card bg={cardBg} borderColor={borderColor}>
// // //               <CardBody p={3}>
// // //                 <Heading size="sm" mb={3} color={headingColor}>
// // //                   Map Legend
// // //                 </Heading>
// // //                 <VStack align="stretch" spacing={2}>
// // //                   <Flex align="center" gap={2}>
// // //                     <Box
// // //                       w="20px"
// // //                       h="20px"
// // //                       bg="#0175c2"
// // //                       borderRadius="full"
// // //                       border="2px solid white"
// // //                     />
// // //                     <Text fontSize="xs" color={textColor}>
// // //                       Vehicle Location
// // //                     </Text>
// // //                   </Flex>
// // //                   <Flex align="center" gap={2}>
// // //                     <Box
// // //                       w="20px"
// // //                       h="25px"
// // //                       bg="#dc3545"
// // //                       clipPath="polygon(50% 0%, 100% 38%, 100% 100%, 0% 100%, 0% 38%)"
// // //                       border="2px solid white"
// // //                     />
// // //                     <Text fontSize="xs" color={textColor}>
// // //                       Order Location
// // //                     </Text>
// // //                   </Flex>
// // //                   <Flex align="center" gap={2}>
// // //                     <Box w="20px" h="3px" bg="#0175c2" />
// // //                     <Text fontSize="xs" color={textColor}>
// // //                       Delivery Route
// // //                     </Text>
// // //                   </Flex>
// // //                 </VStack>
// // //               </CardBody>
// // //             </Card> *
// // //           </VStack> */}
// // //         </Flex>
// // //       </Box>
// // //     </Box>
// // //   );
// // // };

// // // export default VehicleOrderMap;
// // // import React, { useEffect, useState } from "react";
// // // import {
// // //   Box,
// // //   Flex,
// // //   Heading,
// // //   Text,
// // //   VStack,
// // //   HStack,
// // //   Badge,
// // //   useColorModeValue,
// // //   Spinner,
// // //   Select,
// // //   FormControl,
// // //   FormLabel,
// // //   Card,
// // //   CardBody,
// // //   Avatar,
// // //   Divider,
// // // } from "@chakra-ui/react";
// // // import {
// // //   GoogleMap,
// // //   useLoadScript,
// // //   Marker,
// // //   InfoWindow,
// // //   DirectionsRenderer,
// // // } from "@react-google-maps/api";
// // // import { FiTruck, FiUser, FiMapPin, FiNavigation } from "react-icons/fi";
// // // import { MdLocalShipping, MdLocationOn } from "react-icons/md";

// // // const VehicleOrderMap = () => {
// // //   // ALL HOOKS MUST BE AT THE TOP BEFORE ANY CONDITIONAL RETURNS
// // //   const [map, setMap] = useState(null);
// // //   const [selectedVehicle, setSelectedVehicle] = useState(null);
// // //   const [selectedOrder, setSelectedOrder] = useState(null);
// // //   const [directions, setDirections] = useState(null);
// // //   const [loading, setLoading] = useState(false);
// // //   const [activeMarker, setActiveMarker] = useState(null);

// // //   // Load Google Maps Script
// // //   const { isLoaded, loadError } = useLoadScript({
// // //     googleMapsApiKey: "AIzaSyDF2pgkAq9rIaeQGY7HL89D6fZe70XIY2I", // Replace with your API key
// // //   });

// // //   // Color mode values - ALL HOOKS BEFORE CONDITIONAL RETURNS
// // //   const cardBg = useColorModeValue("white", "gray.800");
// // //   const borderColor = useColorModeValue("gray.200", "gray.600");
// // //   const textColor = useColorModeValue("gray.600", "gray.400");
// // //   const headingColor = useColorModeValue("gray.700", "gray.200");
// // //   const bgColor = useColorModeValue("gray.50", "gray.900");

// // //   // Sample data - Replace with your actual data from API
// // //   const [vehicles, setVehicles] = useState([
// // //     {
// // //       id: 1,
// // //       vehicleNo: "MP09RD3418",
// // //       driverName: "Arastu Dixit",
// // //       status: "Active",
// // //       location: { lat: 23.2599, lng: 77.4126 }, // Bhopal
// // //     },
// // //     {
// // //       id: 2,
// // //       vehicleNo: "MP43SH4646",
// // //       driverName: "Sumit Kumar",
// // //       status: "Active",
// // //       location: { lat: 23.2156, lng: 77.4304 }, // Bhopal nearby
// // //     },
// // //   ]);

// // //   const [orders, setOrders] = useState([
// // //     {
// // //       id: 1,
// // //       orderNo: "ORD-001",
// // //       customerName: "Rajesh Sharma",
// // //       address: "Arera Colony, Bhopal, MP",
// // //       location: { lat: 23.2443, lng: 77.4346 },
// // //       status: "Pending",
// // //       phone: "+91 9876543210",
// // //     },
// // //     {
// // //       id: 2,
// // //       orderNo: "ORD-002",
// // //       customerName: "Priya Patel",
// // //       address: "MP Nagar, Bhopal, MP",
// // //       location: { lat: 23.2328, lng: 77.4278 },
// // //       status: "In Transit",
// // //       phone: "+91 9876543211",
// // //     },
// // //     {
// // //       id: 3,
// // //       orderNo: "ORD-003",
// // //       customerName: "Amit Verma",
// // //       address: "Kolar Road, Bhopal, MP",
// // //       location: { lat: 23.1766, lng: 77.4414 },
// // //       status: "Pending",
// // //       phone: "+91 9876543212",
// // //     },
// // //   ]);

// // //   // Map container style
// // //   const mapContainerStyle = {
// // //     width: "100%",
// // //     height: "600px",
// // //     borderRadius: "12px",
// // //   };

// // //   // Center of map (Bhopal)
// // //   const center = {
// // //     lat: 23.2599,
// // //     lng: 77.4126,
// // //   };

// // //   // Google Maps options
// // //   const options = {
// // //     disableDefaultUI: false,
// // //     zoomControl: true,
// // //     streetViewControl: false,
// // //     mapTypeControl: false,
// // //     fullscreenControl: true,
// // //   };

// // //   // Handle map load
// // //   const onLoad = (map) => {
// // //     setMap(map);
// // //   };

// // //   // Calculate route between vehicle and order
// // //   const calculateRoute = (vehicleId, orderId) => {
// // //     if (!isLoaded) return;

// // //     const vehicle = vehicles.find((v) => v.id === vehicleId);
// // //     const order = orders.find((o) => o.id === orderId);

// // //     if (!vehicle || !order) return;

// // //     setLoading(true);

// // //     const directionsService = new window.google.maps.DirectionsService();

// // //     directionsService.route(
// // //       {
// // //         origin: vehicle.location,
// // //         destination: order.location,
// // //         travelMode: window.google.maps.TravelMode.DRIVING,
// // //       },
// // //       (result, status) => {
// // //         setLoading(false);
// // //         if (status === window.google.maps.DirectionsStatus.OK) {
// // //           setDirections(result);
// // //           setSelectedVehicle(vehicleId);
// // //           setSelectedOrder(orderId);
// // //         } else {
// // //           console.error("Directions request failed:", status);
// // //         }
// // //       },
// // //     );
// // //   };

// // //   // Handle vehicle selection
// // //   const handleVehicleSelect = (e) => {
// // //     const vehicleId = parseInt(e.target.value);
// // //     setSelectedVehicle(vehicleId);
// // //     setDirections(null);

// // //     // Center map on selected vehicle
// // //     const vehicle = vehicles.find((v) => v.id === vehicleId);
// // //     if (vehicle && map) {
// // //       map.panTo(vehicle.location);
// // //       map.setZoom(13);
// // //     }
// // //   };

// // //   // Handle order selection
// // //   const handleOrderSelect = (e) => {
// // //     const orderId = parseInt(e.target.value);
// // //     setSelectedOrder(orderId);

// // //     // If both vehicle and order are selected, calculate route
// // //     if (selectedVehicle && orderId) {
// // //       calculateRoute(selectedVehicle, orderId);
// // //     }
// // //   };

// // //   // Get vehicle icon URL
// // //   const getVehicleIcon = () => {
// // //     if (!isLoaded || !window.google) return null;

// // //     return {
// // //       url:
// // //         "data:image/svg+xml;charset=UTF-8," +
// // //         encodeURIComponent(`
// // //         <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
// // //           <circle cx="20" cy="20" r="18" fill="#0175c2" stroke="white" stroke-width="3"/>
// // //           <path d="M20 10 L28 25 L20 22 L12 25 Z" fill="white"/>
// // //         </svg>
// // //       `),
// // //       scaledSize: new window.google.maps.Size(40, 40),
// // //     };
// // //   };

// // //   // Get order icon URL
// // //   const getOrderIcon = () => {
// // //     if (!isLoaded || !window.google) return null;

// // //     return {
// // //       url:
// // //         "data:image/svg+xml;charset=UTF-8," +
// // //         encodeURIComponent(`
// // //         <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
// // //           <path d="M20 0 C10 0 2 8 2 18 C2 28 20 50 20 50 C20 50 38 28 38 18 C38 8 30 0 20 0 Z" fill="#dc3545" stroke="white" stroke-width="2"/>
// // //           <circle cx="20" cy="18" r="8" fill="white"/>
// // //         </svg>
// // //       `),
// // //       scaledSize: new window.google.maps.Size(40, 50),
// // //     };
// // //   };

// // //   // CONDITIONAL RETURNS AFTER ALL HOOKS
// // //   // Handle loading error
// // //   if (loadError) {
// // //     return (
// // //       <Box p={4} textAlign="center">
// // //         <Heading size="md" color="red.500" mb={2}>
// // //           Error Loading Maps
// // //         </Heading>
// // //         <Text color={textColor}>
// // //           Failed to load Google Maps. Please check your API key and internet
// // //           connection.
// // //         </Text>
// // //       </Box>
// // //     );
// // //   }

// // //   // Show loading spinner while maps is loading
// // //   if (!isLoaded) {
// // //     return (
// // //       <Flex justify="center" align="center" minH="100vh">
// // //         <VStack>
// // //           <Spinner size="xl" color="#0175c2" thickness="4px" />
// // //           <Text mt={4} color={textColor}>
// // //             Loading Google Maps...
// // //           </Text>
// // //         </VStack>
// // //       </Flex>
// // //     );
// // //   }

// // //   return (
// // //     <Box p={4} minH="100vh" bg={bgColor}>
// // //       <Box maxW="1400px" mx="auto">
// // //         {/* Header */}
// // //         <Flex justify="space-between" align="center" mb={4}>
// // //           <Box>
// // //             <Heading size="lg" mb={1} color="#0175c2">
// // //               Vehicle & Order Tracking
// // //             </Heading>
// // //             <Text color={textColor} fontSize="sm">
// // //               Track vehicle locations and customer order addresses
// // //             </Text>
// // //           </Box>
// // //         </Flex>

// // //         {/* Stats */}
// // //         <Flex gap={3} mb={4}>
// // //           <Card flex={1} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3}>
// // //               <Flex align="center" justify="space-between">
// // //                 <Box>
// // //                   <Text fontSize="xs" color={textColor} mb={1}>
// // //                     Active Vehicles
// // //                   </Text>
// // //                   <Heading size="md" color="#0175c2">
// // //                     {vehicles.filter((v) => v.status === "Active").length}
// // //                   </Heading>
// // //                 </Box>
// // //                 <Box bg="#E6F4FA" p={2} borderRadius="md">
// // //                   <FiTruck size={20} color="#0175c2" />
// // //                 </Box>
// // //               </Flex>
// // //             </CardBody>
// // //           </Card>

// // //           <Card flex={1} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3}>
// // //               <Flex align="center" justify="space-between">
// // //                 <Box>
// // //                   <Text fontSize="xs" color={textColor} mb={1}>
// // //                     Pending Orders
// // //                   </Text>
// // //                   <Heading size="md" color="#0175c2">
// // //                     {orders.filter((o) => o.status === "Pending").length}
// // //                   </Heading>
// // //                 </Box>
// // //                 <Box bg="#E6F4FA" p={2} borderRadius="md">
// // //                   <MdLocalShipping size={20} color="#0175c2" />
// // //                 </Box>
// // //               </Flex>
// // //             </CardBody>
// // //           </Card>

// // //           <Card flex={1} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3}>
// // //               <Flex align="center" justify="space-between">
// // //                 <Box>
// // //                   <Text fontSize="xs" color={textColor} mb={1}>
// // //                     Total Orders
// // //                   </Text>
// // //                   <Heading size="md" color="#0175c2">
// // //                     {orders.length}
// // //                   </Heading>
// // //                 </Box>
// // //                 <Box bg="#E6F4FA" p={2} borderRadius="md">
// // //                   <MdLocationOn size={20} color="#0175c2" />
// // //                 </Box>
// // //               </Flex>
// // //             </CardBody>
// // //           </Card>
// // //         </Flex>

// // //         {/* Filters */}
// // //         <Card bg={cardBg} mb={4} borderColor={borderColor}>
// // //           <CardBody p={3}>
// // //             <Flex gap={3}>
// // //               <FormControl flex={1}>
// // //                 <FormLabel fontSize="sm" mb={1} color={headingColor}>
// // //                   Select Vehicle
// // //                 </FormLabel>
// // //                 <Select
// // //                   size="sm"
// // //                   placeholder="Choose a vehicle"
// // //                   onChange={handleVehicleSelect}
// // //                   value={selectedVehicle || ""}
// // //                   _focus={{
// // //                     borderColor: "#0175c2",
// // //                     boxShadow: "0 0 0 1px #0175c2",
// // //                   }}
// // //                 >
// // //                   {vehicles.map((vehicle) => (
// // //                     <option key={vehicle.id} value={vehicle.id}>
// // //                       {vehicle.vehicleNo} - {vehicle.driverName}
// // //                     </option>
// // //                   ))}
// // //                 </Select>
// // //               </FormControl>

// // //               <FormControl flex={1}>
// // //                 <FormLabel fontSize="sm" mb={1} color={headingColor}>
// // //                   Select Order
// // //                 </FormLabel>
// // //                 <Select
// // //                   size="sm"
// // //                   placeholder="Choose an order"
// // //                   onChange={handleOrderSelect}
// // //                   value={selectedOrder || ""}
// // //                   disabled={!selectedVehicle}
// // //                   _focus={{
// // //                     borderColor: "#0175c2",
// // //                     boxShadow: "0 0 0 1px #0175c2",
// // //                   }}
// // //                 >
// // //                   {orders.map((order) => (
// // //                     <option key={order.id} value={order.id}>
// // //                       {order.orderNo} - {order.customerName}
// // //                     </option>
// // //                   ))}
// // //                 </Select>
// // //               </FormControl>
// // //             </Flex>
// // //           </CardBody>
// // //         </Card>

// // //         {/* Map and Details */}
// // //         <Flex gap={3}>
// // //           {/* Map */}
// // //           <Card flex={2} bg={cardBg} borderColor={borderColor}>
// // //             <CardBody p={3} position="relative">
// // //               <GoogleMap
// // //                 mapContainerStyle={mapContainerStyle}
// // //                 center={center}
// // //                 zoom={12}
// // //                 options={options}
// // //                 onLoad={onLoad}
// // //               >
// // //                 {/* Vehicle Markers */}
// // //                 {vehicles.map((vehicle) => (
// // //                   <Marker
// // //                     key={`vehicle-${vehicle.id}`}
// // //                     position={vehicle.location}
// // //                     icon={getVehicleIcon()}
// // //                     onClick={() => setActiveMarker(`vehicle-${vehicle.id}`)}
// // //                   >
// // //                     {activeMarker === `vehicle-${vehicle.id}` && (
// // //                       <InfoWindow onCloseClick={() => setActiveMarker(null)}>
// // //                         <Box p={2}>
// // //                           <Heading size="xs" mb={1} color="#0175c2">
// // //                             {vehicle.vehicleNo}
// // //                           </Heading>
// // //                           <Text fontSize="xs" mb={1}>
// // //                             Driver: {vehicle.driverName}
// // //                           </Text>
// // //                           <Badge colorScheme="green" fontSize="9px">
// // //                             {vehicle.status}
// // //                           </Badge>
// // //                         </Box>
// // //                       </InfoWindow>
// // //                     )}
// // //                   </Marker>
// // //                 ))}

// // //                 {/* Order Markers */}
// // //                 {orders.map((order) => (
// // //                   <Marker
// // //                     key={`order-${order.id}`}
// // //                     position={order.location}
// // //                     icon={getOrderIcon()}
// // //                     onClick={() => setActiveMarker(`order-${order.id}`)}
// // //                   >
// // //                     {activeMarker === `order-${order.id}` && (
// // //                       <InfoWindow onCloseClick={() => setActiveMarker(null)}>
// // //                         <Box p={2}>
// // //                           <Heading size="xs" mb={1} color="#dc3545">
// // //                             {order.orderNo}
// // //                           </Heading>
// // //                           <Text fontSize="xs" mb={1}>
// // //                             {order.customerName}
// // //                           </Text>
// // //                           <Text fontSize="xs" mb={1} color="gray.600">
// // //                             {order.address}
// // //                           </Text>
// // //                           <Badge
// // //                             colorScheme={
// // //                               order.status === "Pending" ? "orange" : "blue"
// // //                             }
// // //                             fontSize="9px"
// // //                           >
// // //                             {order.status}
// // //                           </Badge>
// // //                         </Box>
// // //                       </InfoWindow>
// // //                     )}
// // //                   </Marker>
// // //                 ))}

// // //                 {/* Directions */}
// // //                 {directions && (
// // //                   <DirectionsRenderer
// // //                     directions={directions}
// // //                     options={{
// // //                       polylineOptions: {
// // //                         strokeColor: "#0175c2",
// // //                         strokeWeight: 5,
// // //                       },
// // //                     }}
// // //                   />
// // //                 )}
// // //               </GoogleMap>

// // //               {loading && (
// // //                 <Flex
// // //                   position="absolute"
// // //                   top="50%"
// // //                   left="50%"
// // //                   transform="translate(-50%, -50%)"
// // //                   bg="whiteAlpha.900"
// // //                   p={4}
// // //                   borderRadius="md"
// // //                   zIndex={10}
// // //                 >
// // //                   <Spinner color="#0175c2" />
// // //                   <Text ml={3}>Calculating route...</Text>
// // //                 </Flex>
// // //               )}
// // //             </CardBody>
// // //           </Card>

// // //           {/* Details Panel */}
// // //           <VStack flex={1} spacing={3} align="stretch">
// // //             {/* Selected Vehicle Details */}
// // //             {selectedVehicle && (
// // //               <Card bg={cardBg} borderColor={borderColor}>
// // //                 <CardBody p={3}>
// // //                   <Flex align="center" gap={2} mb={2}>
// // //                     <Box bg="#0175c2" p={2} borderRadius="md" color="white">
// // //                       <FiTruck size={16} />
// // //                     </Box>
// // //                     <Heading size="sm" color={headingColor}>
// // //                       Vehicle Details
// // //                     </Heading>
// // //                   </Flex>
// // //                   <Divider mb={2} />
// // //                   {(() => {
// // //                     const vehicle = vehicles.find(
// // //                       (v) => v.id === selectedVehicle,
// // //                     );
// // //                     return vehicle ? (
// // //                       <VStack align="stretch" spacing={2}>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Vehicle Number
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {vehicle.vehicleNo}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Driver Name
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {vehicle.driverName}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Status
// // //                           </Text>
// // //                           <Badge colorScheme="green" fontSize="9px">
// // //                             {vehicle.status}
// // //                           </Badge>
// // //                         </Box>
// // //                       </VStack>
// // //                     ) : null;
// // //                   })()}
// // //                 </CardBody>
// // //               </Card>
// // //             )}

// // //             {/* Selected Order Details */}
// // //             {selectedOrder && (
// // //               <Card bg={cardBg} borderColor={borderColor}>
// // //                 <CardBody p={3}>
// // //                   <Flex align="center" gap={2} mb={2}>
// // //                     <Box bg="#dc3545" p={2} borderRadius="md" color="white">
// // //                       <MdLocationOn size={16} />
// // //                     </Box>
// // //                     <Heading size="sm" color={headingColor}>
// // //                       Order Details
// // //                     </Heading>
// // //                   </Flex>
// // //                   <Divider mb={2} />
// // //                   {(() => {
// // //                     const order = orders.find((o) => o.id === selectedOrder);
// // //                     return order ? (
// // //                       <VStack align="stretch" spacing={2}>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Order Number
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.orderNo}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Customer Name
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.customerName}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Phone
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.phone}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Delivery Address
// // //                           </Text>
// // //                           <Text
// // //                             fontSize="sm"
// // //                             fontWeight="bold"
// // //                             color={headingColor}
// // //                           >
// // //                             {order.address}
// // //                           </Text>
// // //                         </Box>
// // //                         <Box>
// // //                           <Text fontSize="9px" color={textColor}>
// // //                             Status
// // //                           </Text>
// // //                           <Badge
// // //                             colorScheme={
// // //                               order.status === "Pending" ? "orange" : "blue"
// // //                             }
// // //                             fontSize="9px"
// // //                           >
// // //                             {order.status}
// // //                           </Badge>
// // //                         </Box>
// // //                       </VStack>
// // //                     ) : null;
// // //                   })()}
// // //                 </CardBody>
// // //               </Card>
// // //             )}

// // //             {/* Legend */}
// // //             <Card bg={cardBg} borderColor={borderColor}>
// // //               <CardBody p={3}>
// // //                 <Heading size="sm" mb={3} color={headingColor}>
// // //                   Map Legend
// // //                 </Heading>
// // //                 <VStack align="stretch" spacing={2}>
// // //                   <Flex align="center" gap={2}>
// // //                     <Box
// // //                       w="20px"
// // //                       h="20px"
// // //                       bg="#0175c2"
// // //                       borderRadius="full"
// // //                       border="2px solid white"
// // //                     />
// // //                     <Text fontSize="xs" color={textColor}>
// // //                       Vehicle Location
// // //                     </Text>
// // //                   </Flex>
// // //                   <Flex align="center" gap={2}>
// // //                     <Box
// // //                       w="20px"
// // //                       h="25px"
// // //                       bg="#dc3545"
// // //                       clipPath="polygon(50% 0%, 100% 38%, 100% 100%, 0% 100%, 0% 38%)"
// // //                       border="2px solid white"
// // //                     />
// // //                     <Text fontSize="xs" color={textColor}>
// // //                       Order Location
// // //                     </Text>
// // //                   </Flex>
// // //                   <Flex align="center" gap={2}>
// // //                     <Box w="20px" h="3px" bg="#0175c2" />
// // //                     <Text fontSize="xs" color={textColor}>
// // //                       Delivery Route
// // //                     </Text>
// // //                   </Flex>
// // //                 </VStack>
// // //               </CardBody>
// // //             </Card>
// // //           </VStack>
// // //         </Flex>
// // //       </Box>
// // //     </Box>
// // //   );
// // // };

// // // export default VehicleOrderMap;



